// src/utils/componentLookup.ts
var getPropsByName = (props) => {
  const { name, components } = props;
  const componentEntry = components?.find(
    (component) => component?.metadata.name === name
  );
  if (!componentEntry) {
    return null;
  }
  const featureProps = {
    ...componentEntry?.props || {},
    ...{ componentName: componentEntry?.metadata.name },
    ...{ variant: componentEntry?.metadata.variant }
  };
  return componentEntry?.actions ? { ...featureProps, actions: componentEntry?.actions } : featureProps;
};

// src/utils/isObject.tsx
function isObject(value) {
  return typeof value === "object" && value !== null && !Array.isArray(value) && Object.getPrototypeOf(value) === Object.prototype;
}

// src/utils/mapToArray.tsx
function mapToArray(object) {
  return Object.entries(object).map(([name, answer]) => ({ name, answer }));
}

// src/utils/shallowEqual.ts
var shallowEqual = (a, b) => {
  if (a === b) return true;
  const keysA = Object.keys(a);
  const keysB = Object.keys(b);
  if (keysA.length !== keysB.length) return false;
  return keysA.every(
    (key) => Object.prototype.hasOwnProperty.call(b, key) && a[key] === b[key]
  );
};

// src/utils/text.ts
var titleCase = (text) => {
  const transformed = text.toLowerCase().split(" ");
  for (let i = 0; i < transformed.length; i++) {
    transformed[i] = transformed[i].charAt(0).toUpperCase() + transformed[i].slice(1);
  }
  return transformed.join(" ");
};
var snakeCase = (text = "") => text.toLowerCase().replace(/ /g, "_");
var toPascalCase = (text = "") => text.replace(/[_\-\s]+/g, " ").split(" ").map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join("");
var camelToTitleCase = (text) => {
  const withSpaces = text.replace(/([A-Z])/g, " $1").toLowerCase();
  const titleCased = titleCase(withSpaces);
  return titleCased;
};
var camelToSnakeCase = (text) => text.charAt(0).toLowerCase() + text.slice(1).replace(
  /(\[.*?\])|[A-Z]/g,
  (match, group) => group ? match : `_${match.toLowerCase()}`
);
var snakeCaseKeys = (object) => Object.entries(object || {}).reduce(
  (acc, [key, value]) => {
    const newKey = camelToSnakeCase(key);
    if (Array.isArray(value) && value.every(isObject)) {
      return {
        ...acc,
        [newKey]: value.map((v) => snakeCaseKeys(v))
      };
    }
    if (isObject(value)) {
      return {
        ...acc,
        [newKey]: snakeCaseKeys(value)
      };
    }
    return {
      ...acc,
      [newKey]: value
    };
  },
  {}
);
var isNumeric = (str = "") => {
  return !isNaN(Number(str)) && !isNaN(parseFloat(str));
};
var isCurrency = (string = "") => {
  const number = string.replace(/£/g, "");
  return isNumeric(number);
};
var containsHtmlMarkup = (text) => {
  const validElements = [
    "strong",
    "b",
    "p",
    "ul",
    "li",
    "ol",
    "i",
    "em",
    "table",
    "tr",
    "td",
    "br",
    // regex will handle the <br/> and <br /> versions
    "div",
    "a",
    "img"
  ];
  const validElementsPattern = validElements.join("|");
  const htmlTagRegex = new RegExp(`<(${validElementsPattern})\\b[^>]*>`, "i");
  return htmlTagRegex.test(text);
};

// src/constants/addresses.ts
var LINK_ADDRESS_QUESTION_NAME = "Same correspondence address pre quote";
var LINKED_ADDRESS_QUESTION_NAME = "UK correspondence address lookup";
var QUESTIONS_WITH_TOGGLE = [
  "uk_postcode",
  "customer[uk_postcode]"
];

// src/constants/errors.ts
var ERRORS = {
  REQUIRED: "Please provide an answer for this question",
  INVALID_EMAIL: "Please enter a valid email address",
  INVALID_POSTCODE: "Enter a UK postcode, or contact us for help",
  INVALID_TEL: "Enter a valid phone number or contact us for help",
  INVALID_ZIPCODE: "Please enter a valid 5-digit ZIP code",
  INVALID_GENERIC: "The value entered is not valid."
};

// src/constants/constants.ts
var ANNUAL = "annual";
var YEARLY = "yearly";
var MONTHLY = "monthly";
var AIRBRAKE_PROJECT_ID = 90865;
var AIRBRAKE_PROJECT_KEY = "8f2c6b9818d527c9e959af92547e1f03";
var FOCUSABLE_ELEMENT_SELECTOR = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
var BUSINESS_CITY_KEY = "Business city";
var US_STATE_ANSWER_KEY = "US state";
var LOCALE_EN_GB = "en-GB";
var LOCALE_EN_US = "en-US";
var DEFAULT_LOCALE = LOCALE_EN_GB;
var PREPOP_ENDPOINT_URL = "?step_action=prepopulate";
var creditRatingAgencyDescriptions = {
  ambest: {
    label: "A.M. Best",
    description: "The world\u2019s largest credit rating agency specializing in the insurance industry. A.M. Best assesses the creditworthiness of and/or reports on insurance companies."
  },
  demotech: {
    label: "Demotech Rating",
    description: "Demotech is a financial analysis firm specializing in evaluating the financial stability of regional and specialty insurers."
  },
  // This is needed for the empty (UK) case
  "": {
    label: "",
    description: ""
  }
};

// src/utils/transformDates.ts
var formatDateByLocale = (date, locale = LOCALE_EN_GB) => {
  const [year, month, day] = date.split("-");
  if (locale === LOCALE_EN_US) {
    return `${month}/${day}/${year}`;
  }
  return `${day}/${month}/${year}`;
};
var toISODate = (date, locale = LOCALE_EN_GB) => {
  if (locale === LOCALE_EN_GB) {
    const [day2, month2, year2] = date.split("/");
    return `${year2}-${month2}-${day2}`;
  }
  const [month, day, year] = date.split("/");
  return `${year}-${month}-${day}`;
};

// src/utils/transformAnswers.tsx
var createQuestionMetadataMap = (sections) => {
  const questionMap = /* @__PURE__ */ new Map();
  return sections.reduce((map, section) => {
    section.questions.forEach(
      ({ name, id, display }) => map.set(name, { id, type: display?.type })
    );
    return map;
  }, questionMap);
};
var shouldExclude = (q, visibility) => {
  return q.includeAnswer === false || typeof q.includeAnswer === "undefined" && !visibility[q.name] || q.display?.type === "percentage_split";
};
var removeHiddenAnswers = (answers, sections, visibility) => {
  const answersToExclude = new Set(
    sections.flatMap((section) => section.questions).filter((q) => shouldExclude(q, visibility)).map((q) => q.name)
  );
  return Object.fromEntries(
    Object.entries(answers).filter(([key]) => !answersToExclude.has(key))
  );
};
var transformAnswerValue = (value, type, locale) => {
  if (type === "date" && typeof value === "string") {
    return formatDateByLocale(value, locale);
  }
  return value;
};
var transformAddressLookupAnswers = (answers, sections) => {
  const { addressQuestions, fieldMappings } = sections.reduce(
    (acc, section) => {
      section.questions.filter((q) => q.display?.type === "address_lookup").forEach((question) => {
        acc.addressQuestions.add(question.name);
        const mappedFields = question.display.config?.mappedFields;
        if (mappedFields && Array.isArray(mappedFields)) {
          mappedFields.forEach((field) => {
            const fullKey = `${question.id}[${field.id}]`;
            if (field.associatedQuestionId) {
              acc.fieldMappings[field.associatedQuestionId] = fullKey;
            }
          });
        }
      });
      return acc;
    },
    { addressQuestions: /* @__PURE__ */ new Set(), fieldMappings: {} }
  );
  const transformed = Object.entries(answers).reduce(
    (acc, [key, value]) => {
      if (addressQuestions.has(key)) {
        return acc;
      }
      acc[key] = value;
      if (fieldMappings[key]) {
        acc[fieldMappings[key]] = value;
      }
      return acc;
    },
    {}
  );
  return transformed;
};
var transformAnswers = (answers, sections, visibility = {}, locale) => {
  const withTransformedAddressLookupAnswers = transformAddressLookupAnswers(
    answers,
    sections
  );
  const questionMetadata = createQuestionMetadataMap(sections);
  const filteredAnswers = removeHiddenAnswers(
    withTransformedAddressLookupAnswers,
    sections,
    visibility
  );
  return Object.entries(filteredAnswers).reduce(
    (acc, [key, value]) => {
      const newKey = questionMetadata.get(key)?.id || key;
      acc[newKey] = transformAnswerValue(
        value,
        questionMetadata.get(key)?.type,
        locale
      );
      return acc;
    },
    {}
  );
};

// src/utils/typeGuards.ts
var isOneOf = (value, array) => {
  return array.includes(value);
};

// src/hooks/experiments/useExperiment.ts
import { useAtomValue } from "jotai";

// src/store.ts
import { atom } from "jotai";
var sliderStateAtom = atom(false);
var paymentToggleAtom = atom(ANNUAL);
var experimentsAtom = atom(/* @__PURE__ */ new Map());
var localeAtom = atom("en-GB");
var tokensAtom = atom({
  authenticityToken: "",
  journeyToken: ""
});
var quoteComparisonPageAtom = atom(
  []
);
var environmentAtom = atom({ site: "", journeyId: "" });
var questionnaireFormUrlAtom = atom("");
var personalisedCoverPageAtom = atom([]);
var qcpLayoutToggleAtom = atom("list");
var redirectAtom = atom(void 0);
var rewardBannerAtom = atom();
var seeMoreCoversStateAtom = atom(false);

// src/hooks/experiments/useExperiment.ts
var useExperiment = (experiment) => {
  const allExperiments = useAtomValue(experimentsAtom);
  return allExperiments.get(experiment);
};

// src/hooks/experiments/useExperiments.ts
import { useHydrateAtoms } from "jotai/utils";
var useExperiments = (initialExperiments) => {
  const experimentMap = new Map(
    initialExperiments ? Object.entries(initialExperiments) : []
  );
  useHydrateAtoms([[experimentsAtom, experimentMap]]);
};

// src/components/ErrorBoundary/ErrorBoundary.tsx
import { getAirbrake } from "@simplybusiness/services";
import { Component } from "react";

// src/components/ErrorBoundary/GenericError.tsx
import { Container, Link, Text } from "@simplybusiness/mobius";
import { jsx, jsxs } from "react/jsx-runtime";
var GenericError = () => /* @__PURE__ */ jsx(Container, { size: "sm", className: "error-boundary__container", children: /* @__PURE__ */ jsxs("div", { className: "error-boundary__message", children: [
  /* @__PURE__ */ jsx(Text, { elementType: "h1", children: "Oops!" }),
  /* @__PURE__ */ jsxs(Text, { children: [
    "Something went wrong - please",
    " ",
    /* @__PURE__ */ jsx(Link, { href: "javascript:history.go(-1)", children: "go back" }),
    " or try again,",
    /* @__PURE__ */ jsx("br", {}),
    "or call us on 0333 014 6683 if in the UK or (844) 654-7272 if in the US."
  ] })
] }) });

// src/components/ErrorBoundary/ErrorBoundary.tsx
import { jsx as jsx2 } from "react/jsx-runtime";
var ErrorBoundary = class extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error, info) {
    if (true) {
      console.error(error);
    }
    this.setState({ hasError: true });
    if (true) {
      return;
    }
    const airbrake = getAirbrake({
      projectId: AIRBRAKE_PROJECT_ID,
      projectKey: AIRBRAKE_PROJECT_KEY
    });
    void airbrake.notify({
      error,
      params: { info }
    });
  }
  render() {
    const { hasError } = this.state;
    const { children } = this.props;
    if (hasError) {
      return /* @__PURE__ */ jsx2(GenericError, {});
    }
    return children;
  }
};

// src/components/Footer/CompareTheMarketFooter/Footer.tsx
import { Container as Container2 } from "@simplybusiness/mobius";
import classNames from "classnames/dedupe";

// src/components/Footer/CompareTheMarketFooter/FooterContent.tsx
import { Link as Link2, Text as Text2 } from "@simplybusiness/mobius";
import { jsx as jsx3, jsxs as jsxs2 } from "react/jsx-runtime";
var FooterContent = () => {
  return /* @__PURE__ */ jsxs2("div", { className: "mobius-footer-ctm__content", children: [
    /* @__PURE__ */ jsx3(Text2, { elementType: "h2", children: "Important Information" }),
    /* @__PURE__ */ jsxs2("p", { children: [
      "The Comparethemarket Business insurance comparison service is provided by Simply Business. Simply Business is a trading name of Xbridge Limited which is authorised and regulated by the\xA0",
      /* @__PURE__ */ jsx3(
        Link2,
        {
          href: "https://www.fca.org.uk/",
          target: "_blank",
          rel: "noopener noreferrer",
          children: "Financial Conduct Authority"
        }
      ),
      "\xA0(Financial Services Registration No: 313348). Xbridge Limited (No: 3967717) has its registered office at 4th Floor, Hylo, 105 Bunhill Row, London EC1Y 8LZ."
    ] }),
    /* @__PURE__ */ jsx3("p", { children: "Simply Business is not part of Compare the Market Limited." }),
    /* @__PURE__ */ jsxs2("p", { children: [
      "Comparethemarket is a trading name of Compare The Market Limited. Compare The Market Limited is an insurance intermediary, which is authorised and regulated by the\xA0",
      /* @__PURE__ */ jsx3(
        Link2,
        {
          href: "https://www.fca.org.uk/",
          target: "_blank",
          rel: "noopener noreferrer",
          children: "Financial Conduct Authority"
        }
      ),
      "\xA0 (Firm Reference Number 778488)."
    ] }),
    /* @__PURE__ */ jsxs2("p", { children: [
      "For help and information for health, accessibility or financial issues please visit our\xA0",
      /* @__PURE__ */ jsx3(
        Link2,
        {
          href: "https://www.comparethemarket.com/customer-support-hub/",
          target: "_blank",
          rel: "noopener noreferrer",
          children: "Customer Support Hub"
        }
      ),
      "."
    ] }),
    /* @__PURE__ */ jsxs2("p", { children: [
      "To find out how Comparethemarket works please click\xA0",
      /* @__PURE__ */ jsx3(
        Link2,
        {
          href: "https://www.comparethemarket.com/about-us/",
          target: "_blank",
          rel: "noopener noreferrer",
          children: "here"
        }
      ),
      "."
    ] }),
    /* @__PURE__ */ jsxs2("p", { children: [
      "You can find information about how comparethemarket.com process the data that they receive from Simply Business and any data you may provide elsewhere on the comparethemarket.com website in the Comparethemarket\xA0",
      /* @__PURE__ */ jsx3(
        Link2,
        {
          href: "https://www.comparethemarket.com/information/privacy-policy/",
          target: "_blank",
          rel: "noopener noreferrer",
          children: "Privacy and Cookie Policy"
        }
      ),
      "."
    ] }),
    /* @__PURE__ */ jsx3(Text2, { elementType: "h3", variant: "small", children: "Movies, Coffee & More" }),
    /* @__PURE__ */ jsxs2("p", { children: [
      "*To become a Rewards member, a qualifying product must be taken out. 1 membership per year. Discount details, exclusions and restrictions vary per Reward. Full\xA0",
      /* @__PURE__ */ jsx3(
        Link2,
        {
          href: "https://www.comparethemarket.com/information/rewards-terms/",
          target: "_blank",
          rel: "noopener noreferrer",
          children: "T&Cs"
        }
      ),
      "\xA0apply in addition to the below."
    ] }),
    /* @__PURE__ */ jsxs2("ul", { children: [
      /* @__PURE__ */ jsxs2("li", { children: [
        /* @__PURE__ */ jsx3("strong", { children: "Enjoy 2 for 1 cinema tickets: " }),
        " Tuesdays or Wednesdays. Participating cinemas. 2 standard tickets, cheapest free."
      ] }),
      /* @__PURE__ */ jsxs2("li", { children: [
        /* @__PURE__ */ jsx3("strong", { children: "Enjoy 50% off takeaway pizza:" }),
        " App only. Minimum spend, delivery fees & radius vary by outlet. Card only. Geographical restrictions may apply."
      ] }),
      /* @__PURE__ */ jsxs2("li", { children: [
        /* @__PURE__ */ jsx3("strong", { children: "Enjoy 25% off dining out:" }),
        " App only. Days available/no. of diners vary by participating restaurant/outlet. Restrictions may apply to special menus."
      ] }),
      /* @__PURE__ */ jsxs2("li", { children: [
        /* @__PURE__ */ jsx3("strong", { children: "Enjoy 25% off barista-made drinks & pastries:" }),
        " App only. At Caff\xE8 Nero. 2 codes per day."
      ] })
    ] }),
    /* @__PURE__ */ jsxs2("p", { children: [
      "This site is protected by reCAPTCHA and the Google\xA0",
      /* @__PURE__ */ jsx3(
        Link2,
        {
          href: "https://policies.google.com/privacy",
          target: "_blank",
          rel: "noopener noreferrer",
          children: "Privacy Policy"
        }
      ),
      "\xA0and\xA0",
      /* @__PURE__ */ jsx3(
        Link2,
        {
          href: "https://policies.google.com/terms",
          target: "_blank",
          rel: "noopener noreferrer",
          children: "Terms of Service"
        }
      ),
      "\xA0apply."
    ] }),
    /* @__PURE__ */ jsx3("p", { children: "\xA9 2005 - 2025 Comparethemarket All rights reserved." })
  ] });
};

// src/components/Footer/CompareTheMarketFooter/Footer.tsx
import { jsx as jsx4 } from "react/jsx-runtime";
var Footer = (props) => {
  const {
    className,
    phoneNumber,
    openingHours,
    addresses,
    links,
    smallPrint,
    componentName,
    ...otherProps
  } = props;
  const classes = classNames("mobius", "mobius-footer-ctm", className);
  const { variant } = otherProps;
  return /* @__PURE__ */ jsx4(
    "footer",
    {
      ...otherProps,
      ...componentName ? { "data-component": componentName } : {},
      ...variant ? { "data-variant": variant } : {},
      className: classes,
      children: /* @__PURE__ */ jsx4(Container2, { size: "sm", className: "mobius-footer-ctm__content", children: /* @__PURE__ */ jsx4(FooterContent, {}) })
    }
  );
};

// src/components/Footer/Footer.tsx
import { Container as Container3 } from "@simplybusiness/mobius";
import classNames3 from "classnames/dedupe";

// src/components/Footer/Addresses.tsx
import { Grid, Text as Text4 } from "@simplybusiness/mobius";
import classNames2 from "classnames";
import { Fragment } from "react";

// src/components/Footer/Title.tsx
import { Text as Text3 } from "@simplybusiness/mobius";
import { jsx as jsx5 } from "react/jsx-runtime";
var Title = (props) => {
  const { children } = props;
  return /* @__PURE__ */ jsx5(Text3, { className: "mobius-footer__heading", variant: "small", elementType: "h3", children });
};

// src/components/Footer/Addresses.tsx
import { jsx as jsx6, jsxs as jsxs3 } from "react/jsx-runtime";
var Addresses = (props) => {
  const { addresses } = props;
  const classes = classNames2("mobius-footer__addresses", {
    "--is-single": addresses?.length === 1
  });
  if (!addresses) {
    console.warn("Addresses will not render. Prop addresses is empty.");
    return null;
  }
  return /* @__PURE__ */ jsx6("div", { className: classes, children: /* @__PURE__ */ jsxs3("div", { children: [
    /* @__PURE__ */ jsx6(Title, { children: "Address" }),
    /* @__PURE__ */ jsx6(Grid, { columns: addresses.length, columnGap: "sm", children: addresses.map((entry) => /* @__PURE__ */ jsx6(
      Text4,
      {
        variant: "small",
        className: "mobius-footer__address-entry",
        children: /* @__PURE__ */ jsx6("span", { itemProp: "address", children: entry.map((item) => /* @__PURE__ */ jsxs3(Fragment, { children: [
          item,
          /* @__PURE__ */ jsx6("br", {})
        ] }, item)) })
      },
      entry[0]
    )) })
  ] }) });
};

// src/components/Footer/FooterLinks.tsx
import { Flex, Link as Link3, Text as Text5 } from "@simplybusiness/mobius";
import { jsx as jsx7, jsxs as jsxs4 } from "react/jsx-runtime";
var FooterLinks = (props) => {
  const { links } = props;
  if (!links) {
    console.warn("Links will not render. Prop links is empty.");
    return null;
  }
  return /* @__PURE__ */ jsxs4("div", { children: [
    /* @__PURE__ */ jsx7(Title, { children: "Legal" }),
    /* @__PURE__ */ jsx7(Flex, { flexDirection: "column", children: links.map(({ href, text }) => /* @__PURE__ */ jsx7(Text5, { variant: "small", elementType: "span", children: /* @__PURE__ */ jsx7(Link3, { href, target: "_blank", children: text }) }, href)) })
  ] });
};

// src/components/Footer/SmallPrint.tsx
import { TextOrHTML } from "@simplybusiness/mobius";
import { jsx as jsx8 } from "react/jsx-runtime";
var SmallPrint = (props) => {
  const { text } = props;
  if (!text) {
    console.warn("SmallPrint will not render. Prop text is empty.");
    return null;
  }
  return /* @__PURE__ */ jsx8(
    TextOrHTML,
    {
      variant: "legal",
      className: "mobius-footer__small-print",
      text,
      textWrapper: true
    }
  );
};

// src/components/Footer/Support.tsx
import { telephone } from "@simplybusiness/icons";
import { Icon as Icon2, LinkButton, Text as Text8 } from "@simplybusiness/mobius";

// src/components/Footer/OpeningHours.tsx
import { chevronDown } from "@simplybusiness/icons";
import { Icon, Text as Text7 } from "@simplybusiness/mobius";

// src/components/Footer/OpeningHoursEntry.tsx
import { Text as Text6 } from "@simplybusiness/mobius";
import { Fragment as Fragment2, jsx as jsx9, jsxs as jsxs5 } from "react/jsx-runtime";
var OpeningHoursEntry = (props) => {
  const { day, hours } = props;
  return /* @__PURE__ */ jsxs5(Fragment2, { children: [
    /* @__PURE__ */ jsx9(Text6, { variant: "small", elementType: "span", children: day }),
    /* @__PURE__ */ jsx9(Text6, { variant: "small", elementType: "span", children: hours })
  ] });
};

// src/components/Footer/OpeningHours.tsx
import { jsx as jsx10, jsxs as jsxs6 } from "react/jsx-runtime";
var OpeningHours = (props) => {
  const { openingHours } = props;
  if (!openingHours) {
    console.warn("OpeningHours will not render. Prop openingHours is empty.");
    return null;
  }
  return /* @__PURE__ */ jsxs6("details", { className: "mobius-footer__opening-hours-container", children: [
    /* @__PURE__ */ jsxs6("summary", { className: "mobius-footer__opening-hours-toggle", children: [
      /* @__PURE__ */ jsx10(Text7, { variant: "small", elementType: "span", children: "See our opening hours" }),
      /* @__PURE__ */ jsx10(
        Icon,
        {
          className: "mobius-footer__opening-hours-toggle-icon",
          icon: chevronDown,
          size: "xs"
        }
      )
    ] }),
    /* @__PURE__ */ jsx10("ul", { className: "mobius-footer__opening-hours-list", children: openingHours.map((entry) => {
      const { day, hours } = entry;
      return /* @__PURE__ */ jsx10("li", { className: "mobius-footer__opening-hours-list-item", children: /* @__PURE__ */ jsx10(OpeningHoursEntry, { day, hours }) }, day);
    }) })
  ] });
};

// src/components/shared/Underline/Underline.tsx
import { jsx as jsx11, jsxs as jsxs7 } from "react/jsx-runtime";
var Underline = (props) => {
  const { className } = props;
  return /* @__PURE__ */ jsx11(
    "span",
    {
      "aria-hidden": "true",
      style: { display: "block", width: 137, height: 10 },
      children: /* @__PURE__ */ jsxs7(
        "svg",
        {
          className,
          fill: "none",
          xmlns: "http://www.w3.org/2000/svg",
          viewBox: "0 0 137 10",
          children: [
            /* @__PURE__ */ jsx11("title", { children: "Underline icon" }),
            /* @__PURE__ */ jsx11(
              "path",
              {
                d: "M0 7.133c-.01-.758 1.643-1.961 2.66-1.987 1.018-.027 2.98.076 5.89.048 2.906-.024 20.056-.519 25.942-.774 5.885-.255 40.171-1.85 48.603-2.167 8.426-.32 30.733-1.147 35.092-1.26 4.359-.113 13.008-.337 13.734-.458.726-.121 2.385-.97 3.703-.246s1.86 2.83.859 3.965c-1.002 1.135-2.52 1.53-3.689 1.256s-2.769-.434-4.583-.486c-1.82-.055-40.255 1.244-44.18 1.448-3.925.2-34.801 1.36-39.454 1.579-4.647.219-11.474.648-21.5 1.16-10.022.513-16.636.735-18.528.784-1.892.05-3.13-.27-3.796-1.113C.087 8.039.01 7.839 0 7.132Z",
                fill: "currentColor"
              }
            )
          ]
        }
      )
    }
  );
};

// src/components/Footer/Support.tsx
import { Fragment as Fragment3, jsx as jsx12, jsxs as jsxs8 } from "react/jsx-runtime";
var Support = (props) => {
  const { openingHours, phoneNumber } = props;
  return /* @__PURE__ */ jsxs8("div", { className: "mobius-footer__support", children: [
    phoneNumber && /* @__PURE__ */ jsxs8(Fragment3, { children: [
      /* @__PURE__ */ jsxs8(Title, { children: [
        "Need help?",
        " ",
        /* @__PURE__ */ jsx12("span", { className: "mobius-footer__heading-partial", children: "Call us" })
      ] }),
      /* @__PURE__ */ jsxs8(
        LinkButton,
        {
          size: "sm",
          href: `tel:${phoneNumber}`,
          className: "mobius-footer__call-us-link",
          children: [
            /* @__PURE__ */ jsx12(Icon2, { icon: telephone, size: "xs", title: "Call support" }),
            "Call us"
          ]
        }
      ),
      /* @__PURE__ */ jsxs8(
        Text8,
        {
          variant: "h3",
          elementType: "p",
          className: "mobius-footer__phone-number",
          children: [
            phoneNumber,
            /* @__PURE__ */ jsx12(Underline, { className: "mobius-footer__phone-number-underline" })
          ]
        }
      )
    ] }),
    /* @__PURE__ */ jsx12(OpeningHours, { openingHours })
  ] });
};

// src/components/Footer/Footer.tsx
import { jsx as jsx13, jsxs as jsxs9 } from "react/jsx-runtime";
var Footer2 = (props) => {
  const {
    className,
    phoneNumber,
    openingHours,
    addresses,
    links,
    smallPrint,
    componentName,
    ...otherProps
  } = props;
  const classes = classNames3("mobius", "mobius-footer", className);
  const { variant } = otherProps;
  return /* @__PURE__ */ jsx13(
    "footer",
    {
      ...otherProps,
      ...componentName ? { "data-component": componentName } : {},
      ...variant ? { "data-variant": variant } : {},
      className: classes,
      children: /* @__PURE__ */ jsxs9(Container3, { size: "sm", className: "mobius-footer__content", children: [
        /* @__PURE__ */ jsx13(Support, { phoneNumber, openingHours }),
        /* @__PURE__ */ jsx13(Addresses, { addresses }),
        /* @__PURE__ */ jsx13(FooterLinks, { links }),
        /* @__PURE__ */ jsx13(SmallPrint, { text: smallPrint })
      ] })
    }
  );
};

// src/components/Footer/MoneySuperMarketFooter/Footer.tsx
import { Container as Container4, Flex as Flex2, Link as Link4, Text as Text9 } from "@simplybusiness/mobius";
import classNames4 from "classnames/dedupe";

// src/components/Footer/MoneySuperMarketFooter/footerLinks.ts
var footerLinks = [
  {
    text: "Contact Us",
    href: "https://www.moneysupermarket.com/c/customer-care/"
  },
  {
    text: "Investor Relations",
    href: "http://corporate.moneysupermarket.com/"
  },
  {
    text: "Privacy",
    href: "https://www.moneysupermarket.com/legal/privacy-policy/"
  },
  {
    text: "Careers",
    href: "http://www.moneysupermarketjobs.com/"
  },
  {
    text: "News",
    href: "https://www.moneysupermarket.com/press-releases/"
  },
  {
    text: "Terms & Conditions",
    href: "https://www.moneysupermarket.com/legal/terms/"
  },
  {
    text: "Security",
    href: "https://www.moneysupermarket.com/legal/security/"
  },
  {
    text: "Cookie Policy",
    href: "https://www.moneysupermarket.com/legal/cookie-policy/"
  }
];

// src/components/Footer/MoneySuperMarketFooter/Footer.tsx
import { jsx as jsx14, jsxs as jsxs10 } from "react/jsx-runtime";
var Footer3 = (props) => {
  const {
    className,
    phoneNumber,
    openingHours,
    addresses,
    links,
    smallPrint,
    componentName,
    ...otherProps
  } = props;
  const classes = classNames4("mobius", "mobius-footer-msm", className);
  const { variant } = otherProps;
  return /* @__PURE__ */ jsx14(
    "footer",
    {
      ...otherProps,
      ...componentName ? { "data-component": componentName } : {},
      ...variant ? { "data-variant": variant } : {},
      className: classes,
      children: /* @__PURE__ */ jsxs10(Container4, { size: "sm", className: "mobius-footer-msm__content", children: [
        /* @__PURE__ */ jsx14("div", { className: "mobius-footer-msm__logo" }),
        /* @__PURE__ */ jsx14(Flex2, { className: "mobius-footer-msm__links", flexWrap: "wrap", children: footerLinks.map(({ href, text }) => /* @__PURE__ */ jsx14(Link4, { href, target: "_blank", rel: "nofollow", children: text }, href)) }),
        /* @__PURE__ */ jsx14(Text9, { variant: "legal", className: "mobius-footer-msm__small-print", children: smallPrint })
      ] })
    }
  );
};

// src/components/Footer/index.tsx
import { jsx as jsx15 } from "react/jsx-runtime";
var Footer4 = (props) => {
  const { variant } = props;
  switch (variant) {
    case "moneysupermarket":
      return /* @__PURE__ */ jsx15(Footer3, { ...props });
    case "comparethemarket":
      return /* @__PURE__ */ jsx15(Footer, { ...props });
    default:
      return /* @__PURE__ */ jsx15(Footer2, { ...props });
  }
};

// src/components/Header/constants.ts
var OPENING_HOURS_LABEL = "See our opening hours";
var OPENING_HOURS_US = [
  { day: "Mon-Fri", hours: "8am-8pm" },
  { day: "Sat", hours: "Closed" },
  { day: "Sun", hours: "Closed" }
];
var OPENING_HOURS_UK = [
  {
    day: "Thu",
    hours: "9.30am-5.30pm"
  },
  {
    day: "Fri",
    hours: "9.00am-5.30pm"
  },
  {
    day: "Sat",
    hours: "9.00am-2.00pm"
  },
  {
    day: "Sun",
    hours: "Closed"
  },
  {
    day: "Mon",
    hours: "9.00am-5.30pm"
  },
  {
    day: "Tue",
    hours: "9.00am-5.30pm"
  },
  {
    day: "Wed",
    hours: "9.00am-5.30pm"
  }
];
var LOGO_LINK = "https://www.simplybusiness.co.uk/";

// src/components/Header/CompareTheMarketHeader/CompareTheMarketHeader.tsx
import classNames5 from "classnames/dedupe";
import { Container as Container5 } from "@simplybusiness/mobius";

// src/components/Header/Logo.tsx
import { Box, Link as Link5, VisuallyHidden } from "@simplybusiness/mobius";
import { jsx as jsx16 } from "react/jsx-runtime";
var Logo = ({ poweredBySb, url }) => {
  if (!poweredBySb && url) {
    return /* @__PURE__ */ jsx16(Link5, { href: url, className: "mobius-header__logo", children: /* @__PURE__ */ jsx16(VisuallyHidden, { children: "Simply Business homepage" }) });
  }
  return /* @__PURE__ */ jsx16(Box, { className: "mobius-header__logo" });
};

// src/components/Header/ContactDetails.tsx
import { telephone as telephone2 } from "@simplybusiness/icons";
import {
  Button,
  DropdownMenu,
  Flex as Flex3,
  Icon as Icon3,
  Link as Link6,
  LinkButton as LinkButton2
} from "@simplybusiness/mobius";
import { useSnowplowContext } from "@simplybusiness/services";

// src/components/Header/PoweredBySb.tsx
import { jsx as jsx17, jsxs as jsxs11 } from "react/jsx-runtime";
var PoweredBySb = () => {
  return /* @__PURE__ */ jsx17(
    "svg",
    {
      className: "mobius-header__powered-by-icon",
      xmlns: "http://www.w3.org/2000/svg",
      viewBox: "0 0 139 27",
      children: /* @__PURE__ */ jsxs11("g", { fillRule: "evenodd", stroke: "none", strokeWidth: "1", children: [
        /* @__PURE__ */ jsx17(
          "path",
          {
            className: "mobius-header__powered-by-icon-text",
            d: "M78.791 17.327c.255 0 .415.108.495.35l1.592 4.122c.12.337.24.824.24.824h.027s.107-.46.228-.811l1.538-4.12c.08-.257.227-.365.494-.365h.482c.32 0 .468.202.348.513l-2.983 7.526C80.851 26.433 80.01 27 79.06 27c-.388 0-.708-.121-.936-.216-.254-.108-.308-.351-.2-.581l.106-.23c.107-.243.308-.283.495-.23.134.055.308.15.535.15.548 0 .95-.487 1.164-1.027l.334-.784-2.622-6.242c-.133-.31.014-.513.335-.513h.52zm-7.716-.163c1.819 0 2.968 1.46 2.968 3.58 0 2.176-1.297 3.581-3.062 3.581a2.315 2.315 0 01-2.1-1.216h-.026s.04.243.04.595v2.715c0 .298-.147.446-.428.446h-.415c-.294 0-.441-.148-.441-.446v-8.647c0-.297.147-.445.441-.445h.308c.294 0 .441.148.441.377v.406c0 .203-.027.365-.027.365h.027s.575-1.31 2.274-1.31v-.001zm53.437 0c1.872 0 2.915 1.352 2.915 3.257a.453.453 0 01-.455.459h-4.52c.08 1.486 1.137 2.31 2.34 2.31.735 0 1.311-.31 1.619-.54.254-.135.454-.108.601.148l.12.203c.134.23.094.46-.133.595a3.995 3.995 0 01-2.287.73c-2.126 0-3.584-1.554-3.584-3.581 0-2.176 1.458-3.58 3.383-3.58l.001-.001zm-26.668.163c.28 0 .428.148.428.445v3.635c0 .945.2 1.702 1.27 1.702 1.391 0 2.207-1.23 2.207-2.58v-2.757c0-.297.147-.445.441-.445h.415c.28 0 .441.148.441.445v5.945c0 .298-.16.446-.441.446h-.375c-.294 0-.441-.148-.441-.446v-.608c0-.202.04-.391.04-.391h-.026c-.295.675-1.191 1.607-2.515 1.607-1.525 0-2.3-.81-2.3-2.621v-3.932c0-.297.147-.445.441-.445h.415zm-49.608-2.865c1.124 0 2.006.446 2.367.73.228.162.254.365.134.608l-.187.338c-.16.283-.375.256-.602.121-.361-.216-.936-.554-1.738-.554-.977 0-1.699.514-1.699 1.338 0 2.054 4.547 1.5 4.547 4.566 0 1.5-1.11 2.716-2.996 2.716a4.65 4.65 0 01-2.834-.986c-.174-.163-.255-.351-.054-.621l.227-.325c.16-.216.348-.243.602-.081.334.243 1.056.77 2.06.77 1.003 0 1.658-.54 1.658-1.365 0-2.107-4.547-1.634-4.547-4.606 0-1.608 1.337-2.649 3.062-2.649zm88.476 2.702c.936 0 1.551.325 1.872.527.24.122.281.352.147.595l-.12.203c-.134.243-.334.27-.588.148a2.665 2.665 0 00-1.378-.392c-.642 0-1.137.284-1.137.865 0 1.365 3.491.878 3.491 3.202 0 1.162-.976 2.013-2.434 2.013a3.75 3.75 0 01-2.273-.743c-.228-.149-.242-.378-.095-.608l.134-.19c.174-.229.348-.242.616-.094.307.203.869.554 1.658.554.669 0 1.15-.324 1.15-.905 0-1.337-3.49-.892-3.49-3.202 0-1.297 1.123-1.973 2.447-1.973zm-5.766 0c.937 0 1.552.325 1.873.527.24.122.28.352.146.595l-.12.203c-.133.243-.334.27-.588.148a2.664 2.664 0 00-1.377-.392c-.642 0-1.137.284-1.137.865 0 1.365 3.49.878 3.49 3.202 0 1.162-.976 2.013-2.434 2.013-1.123 0-1.9-.46-2.273-.743-.227-.149-.241-.378-.094-.608l.134-.19c.174-.229.347-.242.615-.094.308.203.87.554 1.658.554.67 0 1.15-.324 1.15-.905 0-1.337-3.49-.892-3.49-3.202 0-1.297 1.123-1.973 2.447-1.973zm-23.944 0c.937 0 1.552.325 1.873.527.24.122.281.352.147.595l-.12.203c-.134.243-.335.27-.589.148a2.666 2.666 0 00-1.377-.392c-.642 0-1.137.284-1.137.865 0 1.365 3.49.878 3.49 3.202 0 1.162-.976 2.013-2.433 2.013a3.756 3.756 0 01-2.274-.743c-.227-.149-.24-.378-.094-.608l.134-.19c.174-.229.348-.242.615-.094.308.203.87.554 1.659.554.668 0 1.15-.324 1.15-.905 0-1.337-3.49-.892-3.49-3.202 0-1.297 1.123-1.973 2.446-1.973zm-30.684-2.54c.28 0 .441.15.441.446v7.066c0 .77.335.879.616.906.2.027.347.134.347.391v.324c0 .284-.12.46-.454.46-.723 0-1.806-.203-1.806-1.918V15.07c0-.297.147-.445.441-.445l.415-.001zm35.156 2.703c.294 0 .44.148.44.445v5.945c0 .298-.146.446-.44.446h-.415c-.281 0-.441-.148-.441-.446v-5.945c0-.297.16-.445.44-.445h.416zm-19.111-2.702c1.59 0 2.728.891 2.728 2.431 0 .933-.442 1.676-1.164 2.054v.027c.99.297 1.552 1.243 1.552 2.31 0 1.797-1.378 2.716-3.05 2.716h-2.928c-.295 0-.442-.149-.442-.446V15.07c0-.297.147-.445.442-.445h2.862zm25.137 2.54c1.592 0 2.314.878 2.314 2.62v3.932c0 .298-.147.446-.442.446h-.414c-.281 0-.442-.148-.442-.446v-3.635c0-.959-.187-1.715-1.27-1.715-1.311 0-2.26 1.108-2.26 2.527v2.823c0 .298-.147.446-.442.446h-.414c-.28 0-.442-.148-.442-.446v-5.945c0-.296.162-.445.442-.445h.374c.295 0 .442.149.442.445v.608c0 .203-.04.392-.04.392h.026a2.806 2.806 0 012.568-1.608v.001zm-64.143.162c.294 0 .44.148.44.445v5.945c0 .298-.146.446-.44.446h-.415c-.281 0-.441-.148-.441-.446v-5.945c0-.297.16-.445.441-.445h.415zm10.266-.163c1.538 0 2.233.879 2.233 2.622v3.931c0 .298-.147.446-.44.446H65c-.28 0-.44-.148-.44-.446V20.07c0-.96-.188-1.716-1.19-1.716-1.258 0-2.007 1.31-2.007 2.622v2.742c0 .298-.147.446-.428.446h-.428c-.281 0-.428-.148-.428-.446V20.07c0-.891-.134-1.716-1.177-1.716-1.284 0-2.033 1.352-2.033 2.622v2.742c0 .298-.147.446-.44.446h-.415c-.282 0-.442-.148-.442-.446v-5.945c0-.296.16-.445.442-.445h.374c.295 0 .441.149.441.445v.608c0 .203-.04.392-.04.392h.027c.334-.797 1.284-1.608 2.354-1.608 1.137 0 1.792.527 2.032 1.554h.027c.361-.797 1.298-1.554 2.394-1.554v-.001zm7.196 1.149c-1.016 0-1.952.716-1.952 2.459 0 1.216.669 2.418 1.912 2.418 1.07 0 1.953-.878 1.953-2.432 0-1.486-.79-2.445-1.913-2.445zm21.677 1.432H90.37v3.256h2.126c1.016 0 1.632-.649 1.632-1.622 0-.972-.642-1.634-1.632-1.634zm31.99-1.554c-.99 0-1.792.676-1.98 1.824h3.625c-.04-1.202-.75-1.824-1.645-1.824zm-32.15-2.405h-1.965v2.851h1.993c.856 0 1.39-.608 1.39-1.446 0-.837-.521-1.405-1.417-1.405h-.001zm19.165-1.161c.281 0 .442.148.442.445v.446c0 .284-.16.446-.442.446h-.454c-.295 0-.441-.162-.441-.446v-.446c0-.297.146-.445.44-.445h.455zm-58.117 0c.281 0 .442.148.442.445v.446c0 .284-.16.446-.442.446h-.454c-.294 0-.442-.162-.442-.446v-.446c0-.297.148-.445.442-.445h.454zM99.347 2.948c.168 0 .258.068.314.215l1.582 3.943c.1.283.202.6.202.6h.022s.078-.317.19-.6l1.548-3.932c.056-.158.146-.226.314-.226h.247c.202 0 .291.124.224.328l-2.658 6.527c-.292.736-.874 1.223-1.626 1.223-.325 0-.594-.113-.774-.204a.26.26 0 01-.123-.362l.067-.125c.078-.147.202-.181.337-.136.123.057.291.147.493.147.505 0 .841-.442 1.054-.94l.337-.804-2.22-5.326c-.08-.192.01-.328.212-.328h.258zm-19.592-.136c1.503 0 2.356 1.099 2.356 2.719a.282.282 0 01-.292.283h-4.06c.034 1.45 1.02 2.277 2.187 2.277a2.41 2.41 0 001.48-.52c.157-.103.28-.08.37.067l.09.136c.079.136.056.272-.078.374a3.194 3.194 0 01-1.884.635c-1.683 0-2.95-1.224-2.95-2.98 0-1.87 1.256-2.991 2.781-2.991zm8.27-2.13c.18 0 .27.102.27.283v7.398c0 .182-.09.284-.27.284h-.19c-.18 0-.28-.09-.28-.272v-.43c0-.205.045-.352.045-.352h-.023s-.482 1.19-1.962 1.19c-1.514 0-2.457-1.224-2.457-2.992 0-1.8 1.055-2.98 2.535-2.98a2.022 2.022 0 011.85 1.145h.023s-.034-.193-.034-.43V.965c0-.181.101-.283.28-.283h.213zm5.597 0c.18 0 .28.102.28.283v2.55c0 .26-.045.464-.045.464h.022s.494-1.167 1.94-1.167c1.515 0 2.457 1.224 2.457 2.98 0 1.812-1.066 2.99-2.546 2.99-1.368 0-1.884-1.189-1.884-1.189h-.022s.044.17.044.42v.373c0 .159-.1.26-.28.26h-.18c-.179 0-.268-.101-.268-.283V.965c0-.181.09-.283.269-.283h.213zm-23.718 2.13c1.503 0 2.355 1.099 2.355 2.719a.282.282 0 01-.291.283h-4.06c.033 1.45 1.02 2.277 2.187 2.277a2.41 2.41 0 001.48-.52c.157-.103.28-.08.37.067l.09.136c.079.136.056.272-.079.374a3.195 3.195 0 01-1.884.635c-1.682 0-2.949-1.224-2.949-2.98 0-1.87 1.256-2.991 2.78-2.991h.001zm-15.482 0c1.649 0 2.983 1.269 2.983 2.957 0 1.722-1.334 3.014-2.972 3.014-1.648 0-2.983-1.292-2.983-3.014 0-1.688 1.335-2.957 2.972-2.957zm21.77.102c.202 0 .292.09.292.272v.204c0 .181-.123.283-.314.283-1.189 0-1.76 1.37-1.76 2.56v2.13c0 .182-.09.284-.27.284h-.213c-.18 0-.28-.102-.28-.284V3.231c0-.181.1-.283.28-.283h.202c.168 0 .27.102.27.283v.77a2.3 2.3 0 01-.046.454h.023c.269-.827.92-1.541 1.816-1.541zm-17.687.034c.157 0 .27.068.314.238l1.29 4.01c.05.152.096.324.127.447l.03.12.01.045h.023s.09-.34.18-.612l1.312-3.999c.045-.159.157-.226.314-.226h.269c.157 0 .269.067.314.226l1.312 4c.09.271.18.611.18.611h.022s.067-.328.168-.612l1.29-4.01c.045-.17.157-.238.314-.238h.235c.202 0 .28.124.225.317l-1.728 5.144c-.044.17-.157.238-.313.238h-.348c-.168 0-.27-.068-.314-.238L62.42 4.59c-.09-.284-.169-.612-.169-.612h-.022s-.078.328-.18.612l-1.3 3.818c-.045.17-.146.238-.314.238h-.348c-.168 0-.27-.068-.325-.238l-1.727-5.144c-.056-.193.033-.317.235-.317l.235.001zM48.276.682c1.414 0 2.434.929 2.434 2.402 0 1.484-1.02 2.436-2.434 2.436h-1.951v2.843c0 .182-.1.284-.27.284h-.235c-.18 0-.269-.102-.269-.284V.965c0-.181.09-.283.27-.283h2.455zM85.76 3.514c-.998 0-1.817.839-1.817 2.277 0 1.406.73 2.3 1.783 2.3.943 0 1.828-.68 1.828-2.3 0-1.155-.582-2.277-1.794-2.277zm-31.327-.011c-1.211 0-2.198.974-2.198 2.266 0 1.325.987 2.322 2.198 2.322 1.2 0 2.187-.997 2.187-2.322 0-1.292-.987-2.266-2.187-2.266zm41.275.011c-.942 0-1.829.68-1.829 2.289 0 1.167.584 2.288 1.783 2.288 1.01 0 1.828-.838 1.828-2.288 0-1.394-.74-2.289-1.782-2.289zm-15.975-.068c-.942 0-1.76.635-1.94 1.79h3.544c-.045-1.2-.763-1.79-1.604-1.79zm-9.851 0c-.942 0-1.761.635-1.94 1.79h3.544c-.046-1.2-.763-1.79-1.604-1.79zM48.187 1.385h-1.862v3.432h1.85c1.055 0 1.739-.645 1.739-1.733 0-1.076-.684-1.7-1.727-1.7v.001z"
          }
        ),
        /* @__PURE__ */ jsxs11(
          "g",
          {
            transform: "translate(-.001)",
            className: "mobius-header__powered-by-icon-logo",
            children: [
              /* @__PURE__ */ jsx17("path", { d: "M26.143.304c2.252 0 4.157.727 5.507 2.103 1.244 1.266 1.93 3.012 1.93 4.918 0 1.623-.47 2.952-1.437 4.059 1.023.53 1.834 1.24 2.411 2.113.681 1.03 1.027 2.263 1.027 3.664 0 2.125-.759 4.04-2.136 5.394-1.486 1.462-3.624 2.235-6.182 2.235h-7.237c-1.51 0-2.65-1.151-2.65-2.677V2.98c0-1.55 1.114-2.675 2.65-2.675l6.117-.001zm.001 2.399h-6.117c-.22 0-.27.051-.27.276v19.134c0 .202.072.278.27.278h7.237c1.924 0 3.487-.537 4.52-1.553.914-.899 1.418-2.204 1.418-3.677 0-.92-.21-1.705-.627-2.333-.988-1.719-4.832-2.717-4.832-2.717S31.2 9.998 31.2 7.325c0-1.27-.44-2.417-1.24-3.231-1.13-1.15-2.698-1.391-3.816-1.391z" }),
              /* @__PURE__ */ jsx17("path", { d: "M8.471 0c1.28 0 2.477.192 3.592.575 1.115.384 1.986.81 2.615 1.277.453.323.68.7.68 1.134 0 .256-.084.529-.25.818l-.616 1.118c-.255.5-.604.75-1.047.75-.266 0-.56-.09-.882-.272-.432-.261-.81-.474-1.13-.639-.322-.164-.768-.326-1.34-.485a6.381 6.381 0 00-1.72-.238c-1.12 0-2.013.25-2.678.753-.666.503-.998 1.167-.998 1.993 0 .614.208 1.16.622 1.634.415.474.951.88 1.61 1.214a30.75 30.75 0 002.182.997c.796.33 1.592.698 2.388 1.105.795.408 1.52.869 2.173 1.382.653.514 1.186 1.181 1.597 2.002.412.82.618 1.756.618 2.806 0 .987-.179 1.913-.537 2.778a6.806 6.806 0 01-1.534 2.279c-.666.654-1.505 1.17-2.517 1.547s-2.129.566-3.35.566c-1.517 0-2.925-.26-4.222-.776C2.43 23.8 1.382 23.236.588 22.624a1.798 1.798 0 01-.464-.517.95.95 0 01-.116-.484.425.425 0 010-.167c.022-.256.144-.528.365-.817l.761-1.085c.265-.367.552-.573.86-.617a.64.64 0 01.15-.017c.272 0 .583.108.933.322 1.673 1.21 3.297 1.814 4.872 1.814 1.106 0 1.983-.257 2.63-.77.647-.514.97-1.184.97-2.01 0-.492-.126-.941-.38-1.35a3.632 3.632 0 00-1.038-1.063 12.08 12.08 0 00-1.502-.87 38.76 38.76 0 00-1.775-.822 41.7 41.7 0 01-1.849-.854 12.676 12.676 0 01-1.754-1.047 9.184 9.184 0 01-1.486-1.306A5.245 5.245 0 01.74 9.239a6.314 6.314 0 01-.377-2.22c0-1.386.358-2.617 1.076-3.692.717-1.076 1.69-1.899 2.913-2.47C5.575.284 6.95 0 8.472 0z" })
            ]
          }
        )
      ] })
    }
  );
};

// src/components/Header/ContactDetails.tsx
import { Fragment as Fragment4, jsx as jsx18, jsxs as jsxs12 } from "react/jsx-runtime";
var ContactDetails = ({
  phoneNumber,
  poweredBySb,
  openingHours,
  supportUrl,
  openingHoursLabel
}) => {
  const { snowplow } = useSnowplowContext();
  const handleHoursClick = () => snowplow?.trigger("operatingHoursClick");
  return /* @__PURE__ */ jsxs12(Flex3, { alignItems: "center", children: [
    poweredBySb && /* @__PURE__ */ jsx18(PoweredBySb, {}),
    /* @__PURE__ */ jsxs12(
      Flex3,
      {
        flexDirection: "column",
        alignItems: "end",
        className: "mobius-header__contact --is-desktop",
        children: [
          /* @__PURE__ */ jsx18(Flex3, { gap: "xs", alignItems: "center", children: phoneNumber && /* @__PURE__ */ jsxs12(Fragment4, { children: [
            /* @__PURE__ */ jsx18(
              Icon3,
              {
                className: "mobius-header__phone-icon",
                icon: telephone2,
                title: "Call our support team"
              }
            ),
            /* @__PURE__ */ jsx18("strong", { className: "mobius-header__phone-number", children: phoneNumber })
          ] }) }),
          supportUrl ? /* @__PURE__ */ jsx18(
            Link6,
            {
              className: "mobius-header__contact-link",
              href: supportUrl,
              variant: "secondary",
              onClick: handleHoursClick,
              children: openingHoursLabel
            }
          ) : /* @__PURE__ */ jsx18(
            DropdownMenu,
            {
              trigger: /* @__PURE__ */ jsx18(Button, { variant: "link", onPress: handleHoursClick, children: openingHoursLabel }),
              className: "mobius-header__dropdown",
              children: openingHours?.map(({ day, hours }) => /* @__PURE__ */ jsx18(DropdownMenu.Item, { children: /* @__PURE__ */ jsxs12("span", { children: [
                day,
                " ",
                hours
              ] }) }, day))
            }
          )
        ]
      }
    ),
    /* @__PURE__ */ jsx18(
      Flex3,
      {
        alignItems: "center",
        justifyContent: "center",
        className: "mobius-header__contact --is-mobile",
        children: /* @__PURE__ */ jsxs12(LinkButton2, { "aria-label": "Phone Us", href: `tel:${phoneNumber}`, size: "sm", children: [
          /* @__PURE__ */ jsx18(Icon3, { icon: telephone2, title: "Call our support team" }),
          "Call us"
        ] })
      }
    )
  ] });
};

// src/components/Header/CompareTheMarketHeader/ProvidedBySimplyBusinessLogo.tsx
import { jsx as jsx19, jsxs as jsxs13 } from "react/jsx-runtime";
var ProvidedBySimplyBusinessLogo = () => /* @__PURE__ */ jsxs13(
  "svg",
  {
    xmlns: "http://www.w3.org/2000/svg",
    width: "88",
    height: "24",
    fill: "none",
    viewBox: "0 0 88 24",
    children: [
      /* @__PURE__ */ jsxs13("g", { clipPath: "url(#clip0_530_515)", children: [
        /* @__PURE__ */ jsx19(
          "path",
          {
            fill: "#0E0A2B",
            d: "M44.732 7.382c0-1.612-1.322-2.163-2.49-2.651-.865-.36-1.609-.67-1.609-1.339 0-.56.501-.922 1.277-.922.683 0 1.17.287 1.495.472.125.065.31.145.486.09a.54.54 0 0 0 .301-.287l.223-.405c.17-.308.11-.574-.178-.778-.345-.25-1.217-.675-2.303-.675-1.777 0-3.02 1.065-3.02 2.59 0 1.564 1.308 2.13 2.461 2.63.88.383 1.635.711 1.635 1.399 0 .568-.491.935-1.25.935-.837 0-1.442-.42-1.739-.622l-.014-.01c-.35-.19-.589-.156-.797.13l-.273.392c-.288.392-.096.64.087.795a4.78 4.78 0 0 0 2.736.897c1.75-.002 2.972-1.088 2.972-2.641M47.616 9.297V4.066c0-.372-.202-.574-.568-.574h-.544c-.367 0-.569.205-.569.574v5.231q.002.574.569.574h.544c.372.003.568-.196.568-.574M46.486 2.785h.495c.34 0 .527-.181.527-.512v-.335c0-.332-.192-.523-.527-.523h-.495c-.333 0-.516.186-.516.523v.335c.002.33.183.512.516.512M60.237 15.883h.495c.34 0 .528-.182.528-.513v-.334c0-.333-.193-.523-.527-.523h-.496c-.333 0-.516.185-.516.523v.334c.004.331.183.513.516.513M56.166 3.347c-.998 0-1.56.62-1.923 1.227-.289-.804-.93-1.227-1.868-1.227-.888 0-1.392.573-1.772 1.161V4.07c0-.372-.202-.574-.57-.574h-.48c-.366 0-.567.205-.567.574v5.23c0 .383.192.575.567.575h.554c.371 0 .568-.2.568-.574V7.005c0-1.452.577-2.114 1.336-2.114.651 0 .782.456.782 1.192v3.218c0 .372.206.573.577.573h.545c.372 0 .568-.198.568-.573V7.006c0-1.452.566-2.114 1.312-2.114.673 0 .806.478.806 1.192v3.218c0 .382.2.573.577.573h.533c.374 0 .577-.204.577-.573V5.785c.005-1.62-.71-2.438-2.122-2.438M63.187 10.02c1.742 0 2.958-1.372 2.958-3.338 0-1.998-1.154-3.34-2.872-3.34a2.38 2.38 0 0 0-1.983.98V3.98c0-.299-.223-.49-.568-.49h-.396c-.37 0-.578.21-.578.573v7.688c0 .372.207.573.578.573h.532c.38 0 .577-.199.577-.573V9.544c0-.138-.006-.257-.014-.348a2.17 2.17 0 0 0 1.766.825M61.39 6.719c0-.884.402-1.912 1.539-1.912.896 0 1.5.765 1.5 1.905s-.613 1.88-1.525 1.88c-.993-.004-1.512-.941-1.512-1.873zM69.823 9.347v-.418c0-.287-.16-.472-.439-.502-.218-.023-.44-.123-.44-.69V1.61c0-.366-.208-.573-.569-.573h-.545c-.366 0-.568.204-.568.573v6.252c0 .815.225 1.397.668 1.728.417.31.948.346 1.312.346s.58-.209.58-.588M75.957 3.49h-.62c-.336 0-.546.154-.639.47L73.584 7.17c-.06.2-.126.46-.167.634a9 9 0 0 0-.192-.661L72.037 3.95c-.11-.313-.314-.458-.64-.458h-.673c-.273 0-.4.107-.458.196s-.104.246.007.49l2.392 5.545-.225.53c-.163.382-.496.798-.947.798a1 1 0 0 1-.422-.105h-.01c-.278-.066-.496.044-.627.318l-.11.267a.57.57 0 0 0-.038.467.5.5 0 0 0 .305.278c.286.119.592.184.902.192 1.058 0 1.934-.63 2.334-1.684l2.597-6.61c.068-.191.057-.363-.03-.484-.086-.121-.23-.202-.437-.202M43.986 18.35c.63-.383 1.004-1.069 1.004-1.867 0-1.402-1.094-2.308-2.785-2.308H39.58c-.367 0-.569.205-.569.574v7.689c0 .376.198.573.569.573h2.662c.872 0 1.622-.215 2.167-.622.607-.454.928-1.135.928-1.968 0-.974-.527-1.776-1.351-2.071m-3.27-.614v-2.078h1.462c.66 0 1.058.383 1.058 1.022 0 .638-.399 1.056-1.016 1.056zm2.863 2.591c0 .743-.466 1.205-1.215 1.205h-1.646v-2.398h1.648c.738 0 1.215.468 1.215 1.193zM51.79 16.63h-.529c-.374 0-.577.206-.577.575v2.358c0 1.41-.887 2.051-1.71 2.051-.652 0-.917-.351-.917-1.216v-3.187c0-.372-.206-.573-.578-.573h-.546c-.37 0-.577.21-.577.573v3.51c0 1.62.758 2.442 2.253 2.442a2.56 2.56 0 0 0 2.13-1.14v.417c0 .376.193.573.568.573h.481c.375 0 .577-.204.577-.573V17.21c.006-.37-.206-.58-.575-.58M56.309 19.172c-.595-.204-1.107-.382-1.107-.765 0-.478.497-.554.793-.554.41.013.812.116 1.177.302q.539.234.77-.222l.124-.225c.162-.334.085-.613-.21-.765a3.6 3.6 0 0 0-1.786-.46c-1.48 0-2.475.78-2.475 1.94 0 1.315 1.129 1.712 2.035 2.03.589.206 1.098.382 1.098.759 0 .356-.314.578-.819.578-.599 0-1.045-.24-1.34-.4l-.044-.023c-.343-.177-.584-.134-.783.142l-.149.221a.6.6 0 0 0-.118.468.5.5 0 0 0 .259.316c.63.428 1.375.654 2.138.65 1.45 0 2.463-.82 2.463-1.99-.001-1.304-1.124-1.69-2.026-2.002M60.798 16.63h-.546c-.366 0-.567.206-.567.575v5.232c0 .377.192.573.567.573h.546c.371 0 .568-.198.568-.573V17.21c0-.37-.208-.58-.568-.58M66.85 16.484a2.53 2.53 0 0 0-2.183 1.165v-.444c0-.371-.201-.573-.567-.573h-.481c-.367 0-.568.204-.568.573v5.233c0 .376.193.573.568.573h.545c.372 0 .568-.199.568-.573v-2.42c0-1.154.74-1.991 1.76-1.991.66 0 .93.353.93 1.217v3.194c0 .37.206.573.577.573h.532c.38 0 .577-.199.577-.573v-3.511c.005-1.62-.757-2.443-2.259-2.443M73.648 16.483c-1.924 0-3.27 1.374-3.27 3.34 0 1.936 1.452 3.34 3.454 3.34a4.07 4.07 0 0 0 2.172-.635c.289-.17.364-.451.192-.77l-.143-.244c-.17-.299-.423-.363-.77-.197h-.005c-.398.252-.858.39-1.33.396-1.003 0-1.715-.626-1.827-1.599h3.78a.61.61 0 0 0 .605-.588c0-1.847-1.121-3.043-2.858-3.043m-.038 1.302c.697 0 1.168.495 1.212 1.268h-2.661c.162-.79.716-1.268 1.45-1.268M80.215 19.172c-.595-.204-1.107-.382-1.107-.765 0-.478.497-.554.793-.554.41.014.81.119 1.174.306q.542.234.77-.222l.125-.225c.161-.334.085-.614-.21-.765a3.6 3.6 0 0 0-1.789-.463c-1.48 0-2.474.78-2.474 1.94 0 1.315 1.128 1.712 2.035 2.03.588.206 1.097.382 1.097.759 0 .356-.313.578-.818.578-.6 0-1.046-.24-1.341-.4l-.043-.023c-.344-.177-.585-.134-.784.142l-.15.221a.6.6 0 0 0-.117.468.5.5 0 0 0 .258.316c.63.427 1.376.654 2.14.65 1.45 0 2.462-.82 2.462-1.99.004-1.305-1.12-1.691-2.021-2.003M85.926 19.172c-.594-.204-1.106-.382-1.106-.765 0-.478.497-.554.793-.554.41.013.811.116 1.176.302q.54.234.77-.222l.124-.225c.162-.333.084-.613-.21-.765a3.6 3.6 0 0 0-1.781-.46c-1.48 0-2.475.78-2.475 1.94 0 1.315 1.128 1.712 2.034 2.03.59.206 1.099.382 1.099.759 0 .356-.314.578-.819.578-.599 0-1.045-.24-1.342-.4l-.042-.023c-.343-.177-.585-.134-.784.142l-.149.221a.6.6 0 0 0-.117.468.5.5 0 0 0 .258.316c.63.427 1.376.654 2.139.65 1.45 0 2.463-.82 2.463-1.99-.005-1.304-1.128-1.69-2.03-2.002"
          }
        ),
        /* @__PURE__ */ jsx19(
          "path",
          {
            fill: "url(#paint0_linear_530_515)",
            d: "M14.81 14.474q.599 1.172.598 2.672a6.8 6.8 0 0 1-.52 2.647 6.5 6.5 0 0 1-1.484 2.17q-.967.935-2.436 1.475a9.4 9.4 0 0 1-3.243.538 11.1 11.1 0 0 1-4.087-.741Q1.754 22.497.6 21.622a1.7 1.7 0 0 1-.449-.49.9.9 0 0 1-.112-.462.4.4 0 0 1 0-.158c.04-.288.163-.559.352-.78L1.13 18.7q.385-.524.833-.59a.6.6 0 0 1 .144-.014q.396 0 .903.306 2.43 1.728 4.713 1.728 1.605 0 2.546-.734a2.32 2.32 0 0 0 .94-1.913 2.35 2.35 0 0 0-.37-1.284 3.5 3.5 0 0 0-1.004-1.013q-.697-.466-1.454-.83a37 37 0 0 0-1.718-.781q-.9-.384-1.79-.814a12.3 12.3 0 0 1-1.698-1 9 9 0 0 1-1.443-1.243 5 5 0 0 1-.993-1.644 5.9 5.9 0 0 1-.353-2.116q0-1.977 1.035-3.517A6.7 6.7 0 0 1 4.241.888Q6.02.072 8.231.072a10.8 10.8 0 0 1 3.477.548q1.62.548 2.532 1.216.657.462.656 1.081c-.01.276-.093.545-.24.779L14.06 4.76q-.37.716-1.015.715a1.8 1.8 0 0 1-.853-.26 19 19 0 0 0-1.094-.608A7.3 7.3 0 0 0 9.8 4.145a6.3 6.3 0 0 0-1.666-.225q-1.625 0-2.591.718a2.26 2.26 0 0 0-.962 1.898 2.28 2.28 0 0 0 .602 1.557c.44.481.969.874 1.558 1.157q.955.479 2.116.948a30 30 0 0 1 2.308 1.052c.742.37 1.446.812 2.103 1.317a5.5 5.5 0 0 1 1.542 1.906m20.319 1.901c0 2.068-.752 3.934-2.116 5.26-1.468 1.418-3.577 2.174-6.092 2.174h-6.934c-1.584 0-2.778-1.187-2.778-2.76V3.002a2.66 2.66 0 0 1 .788-1.977 2.7 2.7 0 0 1 1.99-.782h5.862c2.223 0 4.106.71 5.445 2.053 1.237 1.238 1.918 2.945 1.918 4.804 0 1.483-.408 2.715-1.244 3.756a6.16 6.16 0 0 1 2.138 1.933c.68 1.01 1.024 2.217 1.024 3.586zm-7.872-4.964c.032-.02 3.197-1.955 3.197-4.31 0-1.135-.397-2.158-1.12-2.88-1.023-1.027-2.46-1.244-3.486-1.244h-5.882V21.07h6.955c1.78 0 3.221-.483 4.166-1.396.829-.803 1.285-1.974 1.285-3.298 0-.82-.192-1.515-.56-2.069v-.012c-.884-1.516-4.446-2.441-4.482-2.45l-.544-.14z"
          }
        )
      ] }),
      /* @__PURE__ */ jsxs13("defs", { children: [
        /* @__PURE__ */ jsxs13(
          "linearGradient",
          {
            id: "paint0_linear_530_515",
            x1: "29.338",
            x2: "4.353",
            y1: "25.285",
            y2: "0.153",
            gradientUnits: "userSpaceOnUse",
            children: [
              /* @__PURE__ */ jsx19("stop", { stopColor: "#4632D8" }),
              /* @__PURE__ */ jsx19("stop", { offset: "1", stopColor: "#9084E8" })
            ]
          }
        ),
        /* @__PURE__ */ jsx19("clipPath", { id: "clip0_530_515", children: /* @__PURE__ */ jsx19("path", { fill: "#fff", d: "M0 0h88v24H0z" }) })
      ] })
    ]
  }
);

// src/components/Header/CompareTheMarketHeader/ProvidedBySB.tsx
import { jsx as jsx20, jsxs as jsxs14 } from "react/jsx-runtime";
function ProvidedBySB() {
  return /* @__PURE__ */ jsxs14("div", { className: "mobius-header__provided-by", children: [
    "Provided by ",
    /* @__PURE__ */ jsx20(ProvidedBySimplyBusinessLogo, {})
  ] });
}

// src/components/Header/CompareTheMarketHeader/CompareTheMarketHeader.tsx
import { jsx as jsx21, jsxs as jsxs15 } from "react/jsx-runtime";
function CompareTheMarketHeader({
  componentName,
  variant,
  className,
  url,
  productTitle,
  phoneNumber,
  openingHours,
  supportUrl,
  partnerSubHeader,
  partnerSubHeaderText,
  ...otherProps
}) {
  const classes = classNames5("mobius", "mobius-header", className);
  return /* @__PURE__ */ jsx21(
    "header",
    {
      className: classes,
      ...otherProps,
      ...componentName ? { "data-component": componentName } : {},
      ...variant ? { "data-variant": variant } : {},
      children: /* @__PURE__ */ jsx21("div", { className: "mobius-header__container", children: /* @__PURE__ */ jsxs15(Container5, { size: "sm", className: "mobius-header__content", children: [
        /* @__PURE__ */ jsx21(Logo, { url }),
        productTitle && /* @__PURE__ */ jsx21("span", { className: "mobius-header__product-title", children: productTitle }),
        /* @__PURE__ */ jsx21(ProvidedBySB, {}),
        /* @__PURE__ */ jsx21(
          ContactDetails,
          {
            phoneNumber,
            openingHours,
            supportUrl,
            openingHoursLabel: "See our opening hours"
          }
        )
      ] }) })
    }
  );
}

// src/components/Header/Header.tsx
import { Container as Container6 } from "@simplybusiness/mobius";
import classNames7 from "classnames/dedupe";

// src/components/Header/SubHeader.tsx
import { Flex as Flex4, Box as Box2 } from "@simplybusiness/mobius";
import classNames6 from "classnames/dedupe";
import { jsx as jsx22, jsxs as jsxs16 } from "react/jsx-runtime";
var SubHeader = ({
  partnerSubHeaderText,
  partnerName
}) => {
  const subheaderLogoClasses = classNames6("mobius-header__subheader-logo", {
    [`${partnerName}`]: partnerName
  });
  return /* @__PURE__ */ jsx22("div", { className: "mobius-header__subheader", children: /* @__PURE__ */ jsxs16(
    Flex4,
    {
      className: "mobius-header__subheader-content",
      justifyContent: "center",
      alignItems: "center",
      gap: "xxs",
      children: [
        /* @__PURE__ */ jsx22("span", { children: "In partnership with" }),
        partnerSubHeaderText ? /* @__PURE__ */ jsx22("strong", { children: partnerSubHeaderText }) : /* @__PURE__ */ jsx22(Box2, { className: subheaderLogoClasses })
      ]
    }
  ) });
};

// src/components/Header/Header.tsx
import { jsx as jsx23, jsxs as jsxs17 } from "react/jsx-runtime";
var Header = ({
  className,
  poweredBySb = false,
  url,
  phoneNumber,
  openingHours,
  supportUrl,
  openingHoursLabel,
  componentName,
  partnerSubHeader,
  partnerSubHeaderText,
  partnerName,
  productTitle,
  ...otherProps
}) => {
  const classes = classNames7("mobius", "mobius-header", className);
  const { variant } = otherProps;
  return /* @__PURE__ */ jsxs17(
    "header",
    {
      className: classes,
      ...otherProps,
      ...componentName ? { "data-component": componentName } : {},
      ...variant ? { "data-variant": variant } : {},
      children: [
        /* @__PURE__ */ jsx23("div", { className: "mobius-header__container", children: /* @__PURE__ */ jsxs17(Container6, { size: "sm", className: "mobius-header__content", children: [
          /* @__PURE__ */ jsx23(Logo, { url, poweredBySb }),
          productTitle && /* @__PURE__ */ jsx23("span", { className: "mobius-header__product-title", children: productTitle }),
          /* @__PURE__ */ jsx23(
            ContactDetails,
            {
              phoneNumber,
              poweredBySb,
              openingHours,
              supportUrl,
              openingHoursLabel
            }
          )
        ] }) }),
        partnerSubHeader && /* @__PURE__ */ jsx23(
          SubHeader,
          {
            partnerSubHeaderText,
            partnerName
          }
        )
      ]
    }
  );
};

// src/components/Header/index.tsx
import { jsx as jsx24 } from "react/jsx-runtime";
function Header2({ variant, ...props }) {
  if (variant === "comparethemarket") {
    return /* @__PURE__ */ jsx24(CompareTheMarketHeader, { variant, ...props });
  }
  return /* @__PURE__ */ jsx24(Header, { variant, ...props });
}

// src/components/PreFooter/InsuranceBenefits.tsx
import {
  creditCard,
  fileCertificate,
  messageQuestion
} from "@simplybusiness/icons";
import { Container as Container7, Icon as Icon4, Text as Text10 } from "@simplybusiness/mobius";
import { jsx as jsx25, jsxs as jsxs18 } from "react/jsx-runtime";
var BENEFITS = [
  {
    icon: creditCard,
    title: "Buy a policy online",
    description: "Get instant coverage today by paying all at once or choosing a payment plan, all 100% online."
  },
  {
    icon: fileCertificate,
    title: "Instant certificate of insurance (COI)",
    description: "Once you purchase, we'll send your COI via email. You can also access it anytime in your customer account."
  },
  {
    icon: messageQuestion,
    title: "Help after you purchase",
    description: "Have a question or need to make a change? No worries - we're here to help with anything you need."
  }
];
var InsuranceBenefits = () => {
  return /* @__PURE__ */ jsx25("div", { className: "mobius-insurance-benefits", children: /* @__PURE__ */ jsxs18(Container7, { size: "sm", className: "mobius-insurance-benefits__content", children: [
    /* @__PURE__ */ jsx25(Text10, { elementType: "h3", className: "mobius-insurance-benefits__title", children: "Online insurance made easy" }),
    /* @__PURE__ */ jsx25("ul", { className: "mobius-insurance-benefits__list", children: BENEFITS.map(({ icon, title, description }) => /* @__PURE__ */ jsxs18("li", { className: "mobius-insurance-benefits__item", children: [
      /* @__PURE__ */ jsx25(
        Icon4,
        {
          icon,
          size: "md",
          className: "mobius-insurance-benefits__item-icon"
        }
      ),
      /* @__PURE__ */ jsx25(
        Text10,
        {
          variant: "h4",
          className: "mobius-insurance-benefits__item-title",
          children: title
        }
      ),
      /* @__PURE__ */ jsx25(Text10, { className: "mobius-insurance-benefits__item-description", children: description })
    ] }, title)) })
  ] }) });
};

// src/components/PreFooter/PreFooter.tsx
import { Container as Container8, Text as Text11, Trust } from "@simplybusiness/mobius";
import classNames8 from "classnames/dedupe";
import { forwardRef } from "react";

// src/components/PreFooter/LogoFooterShield.tsx
import { jsx as jsx26, jsxs as jsxs19 } from "react/jsx-runtime";
var LogoFooterShield = ({
  className,
  ...props
}) => /* @__PURE__ */ jsxs19(
  "svg",
  {
    width: "20",
    height: "21",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    className,
    ...props,
    children: [
      /* @__PURE__ */ jsx26("title", { children: "Simply Business Shield logo" }),
      /* @__PURE__ */ jsx26("g", { fill: "#F3F3F3", children: /* @__PURE__ */ jsx26("path", { d: "M2.5 5.42188C2.46094 7.14062 2.77344 9.60156 3.78906 12.0234C4.92188 14.5625 6.79688 17.0234 10 18.4688C13.1641 17.0234 15.0391 14.5625 16.1719 12.0234C17.2266 9.60156 17.5391 7.14062 17.5 5.42188L10 2.53125L2.5 5.42188ZM19.3359 4.91406C19.4531 6.86719 19.1406 9.875 17.8906 12.7656C16.6016 15.7344 14.3359 18.625 10.4688 20.3047L10 20.5L9.49219 20.3047C5.625 18.625 3.35938 15.7344 2.07031 12.7656C0.820312 9.875 0.507812 6.86719 0.625 4.91406L0.664062 4.13281L1.40625 3.82031L9.53125 0.695312L10 0.5L10.4297 0.695312L18.5547 3.82031L19.2969 4.13281L19.3359 4.91406ZM14.4141 8.66406L9.41406 13.6641L8.75 14.3281L8.08594 13.6641L5.58594 11.1641L4.92188 10.5L6.25 9.21094L6.91406 9.875L8.75 11.7109L13.0859 7.375L13.75 6.71094L15.0781 8L14.4141 8.66406Z" }) }),
      /* @__PURE__ */ jsx26("defs", { children: /* @__PURE__ */ jsx26("clipPath", { id: "a", children: /* @__PURE__ */ jsx26("path", { fill: "#fff", d: "M0 0H20V21H0z" }) }) })
    ]
  }
);

// src/components/PreFooter/PreFooter.tsx
import { Fragment as Fragment5, jsx as jsx27, jsxs as jsxs20 } from "react/jsx-runtime";
var PreFooter = forwardRef((props, ref) => {
  const { componentName, ...otherProps } = props;
  const classes = classNames8(
    "mobius",
    "mobius-pre-footer",
    otherProps.className
  );
  const { variant } = otherProps;
  const isMinimal = variant === "minimal";
  return /* @__PURE__ */ jsxs20(Fragment5, { children: [
    !isMinimal && /* @__PURE__ */ jsx27(InsuranceBenefits, {}),
    /* @__PURE__ */ jsx27(
      "div",
      {
        ref,
        ...otherProps,
        ...componentName ? { "data-component": componentName } : {},
        ...variant ? { "data-variant": variant } : {},
        className: classes,
        children: /* @__PURE__ */ jsxs20(Container8, { size: "sm", className: "mobius-pre-footer__content", children: [
          /* @__PURE__ */ jsx27(
            Trust,
            {
              locale: "en-US",
              variant: "micro-combo",
              theme: "dark",
              className: "mobius-pre-footer__widget"
            }
          ),
          /* @__PURE__ */ jsx27(LogoFooterShield, {}),
          /* @__PURE__ */ jsx27(Text11, { className: "mobius-pre-footer__text", children: "Insuring over 1 million small business owners worldwide." })
        ] })
      }
    )
  ] });
});
PreFooter.displayName = "PreFooter";

// src/components/ProgressSteps/ProgressStepsItem.tsx
import { tick } from "@simplybusiness/icons";
import { Icon as Icon5, Link as Link7, Text as Text12 } from "@simplybusiness/mobius";
import { useSnowplowContext as useSnowplowContext2 } from "@simplybusiness/services";
import classNames9 from "classnames/dedupe";
import { forwardRef as forwardRef2 } from "react";
import { Fragment as Fragment6, jsx as jsx28, jsxs as jsxs21 } from "react/jsx-runtime";
var ProgressStepsItem = forwardRef2((props, ref) => {
  const {
    isComplete,
    url,
    to,
    name,
    isActive,
    index,
    className,
    activeName,
    activeIndex,
    ...otherProps
  } = props;
  const { snowplow } = useSnowplowContext2();
  const classes = classNames9(
    "mobius",
    "mobius-progress-steps__item",
    {
      "--is-active": isActive,
      "--is-complete": isComplete,
      "--is-incomplete": !isComplete && !isActive
    },
    className
  );
  const linkClasses = classNames9(
    "mobius-progress-steps__item-link",
    "mobius-link"
  );
  const shapeClasses = "mobius-progress-steps__item-shape";
  const nameClasses = "mobius-progress-steps__item-name";
  const nameInnerClasses = "mobius-progress-steps__item-name-inner";
  const ariaLabel = `Step ${index}${name ? `, ${name}` : ""}${isComplete ? ", Complete" : ""}`;
  const handleProgressStepItemClick = () => {
    snowplow?.trigger("progressbarClicked", {
      fromStepIndex: activeIndex,
      fromStepName: activeName,
      stepIndex: index,
      stepName: name
    });
    return false;
  };
  return /* @__PURE__ */ jsx28(
    "li",
    {
      ref,
      "aria-label": ariaLabel,
      "aria-current": isActive ? "step" : void 0,
      ...otherProps,
      className: classes,
      children: isComplete ? /* @__PURE__ */ jsxs21(
        Link7,
        {
          className: linkClasses,
          href: url,
          to,
          onClick: handleProgressStepItemClick,
          children: [
            /* @__PURE__ */ jsx28("div", { className: shapeClasses, children: /* @__PURE__ */ jsx28(
              Icon5,
              {
                icon: tick,
                className: "mobius-progress-steps__item-icon",
                size: "md",
                title: "Step completed"
              }
            ) }),
            /* @__PURE__ */ jsx28(Text12, { variant: "small", className: nameClasses, children: name })
          ]
        }
      ) : /* @__PURE__ */ jsxs21(Fragment6, { children: [
        /* @__PURE__ */ jsx28("div", { className: shapeClasses, children: /* @__PURE__ */ jsx28("span", { className: "mobius-progress-steps__item-number", children: index }) }),
        /* @__PURE__ */ jsx28(Text12, { variant: "small", className: nameClasses, children: /* @__PURE__ */ jsx28("span", { className: nameInnerClasses, children: name }) })
      ] })
    }
  );
});
ProgressStepsItem.displayName = "ProgressStepsItem";

// src/components/ProgressSteps/ProgressSteps.tsx
import { Container as Container9 } from "@simplybusiness/mobius";
import classNames10 from "classnames/dedupe";
import { forwardRef as forwardRef3 } from "react";
import { jsx as jsx29 } from "react/jsx-runtime";
var ProgressSteps = forwardRef3(
  (props, ref) => {
    const { className, steps, componentName, ...otherProps } = props;
    const { variant } = otherProps;
    if (!steps) {
      console.warn("ProgressSteps will not render. Prop steps is empty.");
      return null;
    }
    const classes = classNames10("mobius", "mobius-progress-steps", className);
    const itemCount = steps.length;
    const activeStep = steps.find((step) => step.isActive);
    return /* @__PURE__ */ jsx29(
      Container9,
      {
        size: "sm",
        elementType: "ol",
        ref,
        ...otherProps,
        ...componentName ? { "data-component": componentName } : {},
        ...variant ? { "data-variant": variant } : {},
        className: classes,
        style: { gridTemplateColumns: `repeat(${itemCount}, 1fr)` },
        children: steps.map((step, index) => /* @__PURE__ */ jsx29(
          ProgressStepsItem,
          {
            index: index + 1,
            activeIndex: activeStep?.index,
            activeName: activeStep?.name,
            ...step
          },
          step.name
        ))
      }
    );
  }
);
ProgressSteps.displayName = "ProgressSteps";

// src/contexts/TrustpilotProvider.tsx
import { useEffect } from "react";
import { jsx as jsx30 } from "react/jsx-runtime";
var TrustpilotProvider = ({ children }) => {
  useEffect(() => {
    const script = document.createElement("script");
    script.type = "text/javascript";
    script.className = "optanon-category-C0002";
    script.src = "//widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js";
    script.async = true;
    document.head.appendChild(script);
    return () => {
      document.head.removeChild(script);
    };
  }, []);
  return /* @__PURE__ */ jsx30("div", { children });
};

// src/contexts/ZendeskProvider.tsx
import { useEffect as useEffect2 } from "react";
var getZendeskKeys = (pageData) => pageData.scripts?.find(({ metadata }) => metadata.name === "zendesk-chat")?.props;
var ZendeskProvider = ({ chatKey, customerId }) => {
  useEffect2(() => {
    const script = document.createElement("script");
    script.type = "text/javascript";
    script.id = "ze-snippet";
    script.className = "optanon-category-C0003";
    script.src = `https://static.zdassets.com/ekr/snippet.js?key=${chatKey}`;
    script.async = true;
    script.addEventListener("load", function() {
      if (window?.zE) {
        window.zE("messenger:on", "open", function() {
          window.zE("messenger:set", "conversationFields", [
            { id: customerId, value: window?.location.href }
          ]);
        });
      }
    });
    document.head.appendChild(script);
    return () => {
      document.head.removeChild(script);
    };
  }, [chatKey, customerId]);
  return null;
};

// src/contexts/CtmTrackingProvider.tsx
import { useEffect as useEffect3 } from "react";
var getCtmScriptProps = (pageData) => pageData.scripts?.find(
  ({ metadata }) => metadata.name === "comparethemarket"
)?.props;
var beginObservingQuestionnaire = (affiliateId) => {
  if (window.tracking.Initialise === void 0) {
    console.log(
      "CTM tracking initialize function is not available. Tracking will not be enabled."
    );
    return;
  }
  const visitor = window.tracking.Initialise(
    window.tracking.metaInfo.trackingUrl
  );
  const questionnaire = document.getElementsByClassName(
    "mobius-questionnaire"
  )[0];
  if (window.tracking.isAvailable) {
    visitor.startQuote(window.tracking.metaInfo.productName, affiliateId);
  }
  const emailObserver = new MutationObserver((_mutations, observer) => {
    const customerEmailAddress = questionnaire.querySelector(
      "input[id='customer[email_address]']"
    );
    if (customerEmailAddress) {
      customerEmailAddress.addEventListener("blur", () => {
        if (window.tracking.isAvailable && customerEmailAddress.value.length) {
          visitor.emailDisclosed(
            window.tracking.metaInfo.productName,
            customerEmailAddress.value
          );
          observer.disconnect();
        }
      });
    }
  });
  emailObserver.observe(questionnaire, { childList: true });
  const assumptionsObserver = new MutationObserver((_mutations, observer) => {
    const assumptionSubmit = questionnaire.querySelector(
      ".mobius-submission__questionnaire-form"
    );
    if (assumptionSubmit) {
      assumptionSubmit.addEventListener("submit", () => {
        if (window.tracking.isAvailable) {
          visitor.endQuote(window.tracking.metaInfo.productName, affiliateId);
          observer.disconnect();
        }
      });
    }
  });
  assumptionsObserver.observe(questionnaire, { childList: true });
};
var CtmTrackingProvider = ({
  scriptUrl,
  trackingUrl,
  application,
  affiliateId,
  productName,
  quickToQuote
}) => {
  const ctmTrackingElementId = "comparethemarket-tracking";
  useEffect3(() => {
    if (!quickToQuote) return;
    if (document.getElementById(ctmTrackingElementId) !== null) return;
    window.tracking = window.tracking || {};
    window.tracking.metaInfo = {
      application,
      trackingUrl: "//" + trackingUrl,
      page: document.title,
      productName,
      endQuoteFired: false
    };
    const ctmUrlScript = document.createElement("script");
    ctmUrlScript.id = ctmTrackingElementId;
    ctmUrlScript.src = `//${scriptUrl}`;
    ctmUrlScript.async = true;
    ctmUrlScript.addEventListener("load", () => {
      if (window.tracking.Initialise !== void 0) {
        beginObservingQuestionnaire(affiliateId);
        return;
      }
      const documentObserver = new MutationObserver((_mutations, observer) => {
        if (window.tracking.Initialise !== void 0) {
          observer.disconnect();
          beginObservingQuestionnaire(affiliateId);
        }
      });
      documentObserver.observe(document, { childList: true, subtree: true });
    });
    document.head.appendChild(ctmUrlScript);
    return () => {
      document.head.removeChild(ctmUrlScript);
    };
  }, []);
  return null;
};

// src/contexts/CtmTealiumProvider.tsx
import { useEffect as useEffect4, useRef, useState } from "react";
var getTealiumScriptProps = (pageData) => pageData.scripts?.find(
  ({ metadata }) => metadata.name === "tealium"
)?.props;
var shiftPrefix = (name) => {
  return "data-insights-event-" + name;
};
var getAttributeValue = (element, name) => {
  return element.getAttribute(shiftPrefix(name)) || "";
};
var validEmail = (email) => {
  return /^.+@.+\..+$/.test(email);
};
var debugLog = (message, data) => {
  const logMessage = message + (data ? ` - ${JSON.stringify(data)}` : "");
  if (!window || !window.newrelic) {
    console.log(logMessage);
    return;
  }
  window.newrelic.log(logMessage);
};
var loadTealiumTracking = ({
  appNameField,
  appPlatform,
  appVersion,
  campaignMaskcode,
  customerId,
  customerPartialPostcode,
  enquiryId,
  insurerName,
  pageEventCategory,
  pageEventId,
  pageNameField,
  pageTypeField,
  paymentPreference,
  policyStartDate,
  primaryDetail,
  productDetail
}) => {
  if (!window.utag || !window.tracking) {
    return;
  }
  const utag = window.utag;
  const tracking = window.tracking;
  const hashString = tracking.hashString || ((s) => s);
  const questionnaire = document.getElementsByClassName(
    "mobius-questionnaire"
  )[0];
  const utagData = {
    app_name: appNameField,
    app_platform: appPlatform,
    app_version: appVersion,
    brand_name: insurerName || "",
    campaign_maskcode: campaignMaskcode || "",
    customer_id: customerId ? hashString(customerId) : "",
    customer_partialpostcode: customerPartialPostcode || "",
    enquiry_id: enquiryId || "",
    page_name: pageNameField,
    page_type: pageTypeField,
    policy_start_date: policyStartDate || "",
    product_detail: productDetail || "",
    product_paymenttype: paymentPreference || "",
    trade_type: primaryDetail || ""
  };
  utag.data = { ...utag.data, ...utagData };
  utag.view(utag.data);
  const insightsElements = document.querySelectorAll(
    `[${shiftPrefix("label")}]`
  );
  insightsElements.forEach((element) => {
    const eventType = getAttributeValue(element, "tealium-event");
    if (eventType) {
      element.addEventListener(
        eventType,
        function onceHandler() {
          const eventData = {
            event_category: getAttributeValue(element, "category"),
            event_action: getAttributeValue(element, "action"),
            event_label: getAttributeValue(element, "label")
          };
          utag.data = { ...utag.data, ...eventData };
          utag.link(utag.data);
          element.removeEventListener(eventType, onceHandler);
        },
        { once: true }
      );
    }
  });
  const emailObserver = new MutationObserver((_mutations, observer) => {
    const customerEmailAddress = questionnaire.querySelector(
      "input[id='customer[email_address]']"
    );
    if (customerEmailAddress) {
      debugLog("emailAddressFieldFound");
      let lastEmailAddress = "";
      customerEmailAddress.addEventListener("blur", function() {
        const emailAddress = customerEmailAddress.value;
        if (validEmail(emailAddress) && emailAddress !== lastEmailAddress) {
          lastEmailAddress = emailAddress;
          const eventData = {
            customer_id: hashString(emailAddress.trim()),
            event_category: pageEventCategory,
            event_action: "sendCustomerId",
            event_label: pageEventId,
            event_value: ""
          };
          utag.data = { ...utag.data, ...eventData };
          debugLog("emailAddressBlurred", eventData);
          utag.link(utag.data);
        }
      });
      observer.disconnect();
    }
  });
  emailObserver.observe(questionnaire, { childList: true });
  const fireCelebrusSyntheticAction = (params) => {
    const actionData = {
      event_category: params.syntheticEventCategory,
      event_label: params.syntheticEventLabel,
      event_action: params.syntheticEventAction
    };
    utag.data = { ...utag.data, ...actionData };
    debugLog("fireCelebrusSyntheticAction", { ...utag.data });
    utag.link(utag.data);
  };
  fireCelebrusSyntheticAction({
    syntheticEventAction: "sendQuoteStartedEvent",
    syntheticEventCategory: "QuoteStarted",
    syntheticEventLabel: "QuoteStarted_NewQuote"
  });
  const continueToQuoteButtonObserver = new MutationObserver(() => {
    const ctqButton = questionnaire.querySelector(
      ".mobius-submit-action button"
    );
    if (ctqButton) {
      debugLog("continueToQuoteButtonFound");
      ctqButton.addEventListener(
        "click",
        () => {
          fireCelebrusSyntheticAction({
            syntheticEventAction: "sendQuoteEndedEvent",
            syntheticEventCategory: "QuoteEnded",
            syntheticEventLabel: "QuoteEnded"
          });
        },
        { once: true }
      );
      continueToQuoteButtonObserver.disconnect();
    }
  });
  continueToQuoteButtonObserver.observe(questionnaire, { childList: true });
};
var CtmTealiumProvider = (props) => {
  const [utagScriptLoaded, setUtagScriptLoaded] = useState(false);
  const [tealiumScriptLoaded, setTealiumScriptLoaded] = useState(false);
  const [utagAndTrackingAvailable, setUtagAndTrackingAvailable] = useState(false);
  const providerLoaded = useRef(false);
  const providerInitialized = useRef(false);
  useEffect4(() => {
    if (providerLoaded.current) return;
    if (!props.quickToQuote) return;
    const utagScript = document.createElement("script");
    utagScript.src = `https://tags.tiqcdn.com/utag/ctm/${props.snippetUrl}/utag.js`;
    utagScript.type = "text/javascript";
    utagScript.async = true;
    utagScript.onload = () => {
      setUtagScriptLoaded(true);
    };
    document.head.appendChild(utagScript);
    const tealiumScript = document.createElement("script");
    tealiumScript.src = `https://chronicle.comparethemarket.com/hash.js`;
    tealiumScript.type = "text/javascript";
    tealiumScript.classList = "optanon-category-C0001";
    tealiumScript.async = true;
    tealiumScript.onload = () => {
      setTealiumScriptLoaded(true);
    };
    document.head.appendChild(tealiumScript);
    const documentObserver = new MutationObserver((_mutations, observer) => {
      if (window.utag && window.tracking) {
        setUtagAndTrackingAvailable(true);
        observer.disconnect();
      }
    });
    documentObserver.observe(document, { childList: true, subtree: true });
    return () => {
      document.head.removeChild(tealiumScript);
      document.head.removeChild(utagScript);
      documentObserver.disconnect();
    };
  }, [providerLoaded, props]);
  useEffect4(() => {
    if (providerInitialized.current) {
      return;
    }
    if (utagScriptLoaded && tealiumScriptLoaded && utagAndTrackingAvailable) {
      providerInitialized.current = true;
      loadTealiumTracking(props);
    }
  }, [
    utagScriptLoaded,
    tealiumScriptLoaded,
    utagAndTrackingAvailable,
    providerInitialized,
    props
  ]);
  return null;
};

// src/pages/Page/Page.tsx
import { useEffect as useEffect6, useState as useState2 } from "react";

// src/hooks/environment/useEnvironment.tsx
import { useAtom, useAtomValue as useAtomValue2, useSetAtom } from "jotai";
var useEnvironmentValues = () => useAtomValue2(environmentAtom);
var useSetEnvironmentValues = () => useSetAtom(environmentAtom);

// src/hooks/tokens/useTokens.tsx
import { useAtom as useAtom2, useAtomValue as useAtomValue3, useSetAtom as useSetAtom2 } from "jotai";
var useTokens = () => useAtom2(tokensAtom);
var useTokensValues = () => useAtomValue3(tokensAtom);
var useSetTokens = () => useSetAtom2(tokensAtom);

// src/hooks/useLocale/useLocale.ts
import { useAtomValue as useAtomValue4 } from "jotai";
import { useHydrateAtoms as useHydrateAtoms2 } from "jotai/utils";
import { useEffect as useEffect5 } from "react";
function useLocale(defaultLocale = DEFAULT_LOCALE) {
  useHydrateAtoms2([[localeAtom, defaultLocale]]);
  useEffect5(() => {
    document.documentElement.lang = defaultLocale;
  }, [defaultLocale]);
  const locale = useAtomValue4(localeAtom);
  const isUK = locale === LOCALE_EN_GB;
  const isUS = locale === LOCALE_EN_US;
  const dateFormat = isUK ? "dd/mm/yyyy" : "mm/dd/yyyy";
  return {
    locale,
    isUK,
    isUS,
    dateFormat
  };
}

// src/components/RewardBanner/RewardBanner.tsx
import { Link as Link8, Text as Text13 } from "@simplybusiness/mobius";
import classNames11 from "classnames/dedupe";
import { Fragment as Fragment7, jsx as jsx31, jsxs as jsxs22 } from "react/jsx-runtime";
var RewardBanner = (props) => {
  const {
    componentName,
    description,
    termsUrl,
    code,
    referAFriendId,
    variant,
    ...otherProps
  } = props;
  const classes = classNames11(
    "mobius",
    "mobius-reward-banner",
    variant === "voucher" && "--is-voucher",
    variant === "raf" && "--is-raf"
  );
  return /* @__PURE__ */ jsx31(
    "div",
    {
      className: classes,
      ...otherProps,
      ...componentName ? { "data-component": componentName } : {},
      children: /* @__PURE__ */ jsxs22("div", { className: "mobius-reward-banner__body", children: [
        variant === "voucher" && /* @__PURE__ */ jsxs22(Fragment7, { children: [
          /* @__PURE__ */ jsxs22(Text13, { variant: "small", children: [
            description,
            ".",
            " ",
            /* @__PURE__ */ jsx31(Link8, { href: termsUrl, target: "_blank", rel: "noopener noreferrer", children: "View terms" })
          ] }),
          /* @__PURE__ */ jsxs22(Text13, { variant: "small", children: [
            "Voucher applied ",
            /* @__PURE__ */ jsx31("strong", { children: code })
          ] })
        ] }),
        variant === "raf" && /* @__PURE__ */ jsxs22(Fragment7, { children: [
          /* @__PURE__ */ jsxs22(Text13, { variant: "small", children: [
            "Voucher applied ",
            /* @__PURE__ */ jsx31("strong", { children: code })
          ] }),
          /* @__PURE__ */ jsxs22(Text13, { variant: "small", children: [
            "Referrer ",
            /* @__PURE__ */ jsx31("strong", { children: referAFriendId })
          ] })
        ] })
      ] })
    }
  );
};

// src/hooks/useRewardBanner/useRewardBanner.tsx
import { useAtom as useAtom3 } from "jotai";
import { useHydrateAtoms as useHydrateAtoms3 } from "jotai/utils";
var useSetRewardBanner = (state) => {
  useHydrateAtoms3([[rewardBannerAtom, state]]);
};
var useRewardBanner = () => useAtom3(rewardBannerAtom);

// src/components/IndicativePricingModalIntervention/IndicativePricingModalIntervention.tsx
import {
  InterventionWrapper,
  PricingNudgeIntervention
} from "@simplybusiness/mobius-interventions";
import { jsx as jsx32 } from "react/jsx-runtime";
var IndicativePricingModalInterventionInner = (props) => {
  return props.data?.component_names?.includes("indicative-pricing") ? /* @__PURE__ */ jsx32(PricingNudgeIntervention, { ...props }) : /* @__PURE__ */ jsx32("div", { "data-testid": "indicative-pricing-modal-intervention-control" });
};
var IndicativePricingModalIntervention = (_props) => {
  return /* @__PURE__ */ jsx32("div", { "data-testid": "indicative-pricing-modal-intervention", children: /* @__PURE__ */ jsx32(InterventionWrapper, { name: "ll-q2q-ip-widget-intervention", children: /* @__PURE__ */ jsx32(IndicativePricingModalInterventionInner, {}) }) });
};

// src/pages/Page/Page.tsx
import { jsx as jsx33, jsxs as jsxs23 } from "react/jsx-runtime";
var Page = (props) => {
  const {
    configuration,
    layout: {
      /* metadata, */
      components
      /* variations */
    },
    children,
    experiments
  } = props;
  useExperiments(experiments);
  useLocale(configuration.locale);
  const [pageReady, setPageReady] = useState2(false);
  const {
    authenticityToken,
    journeyToken,
    site,
    journeyId,
    vertical,
    affclie
  } = configuration;
  const setTokens = useSetTokens();
  const setEnvironment = useSetEnvironmentValues();
  useEffect6(() => {
    setTokens({ authenticityToken, journeyToken });
    setEnvironment({ site, journeyId, vertical, affclie });
    setPageReady(true);
  }, [
    authenticityToken,
    journeyToken,
    setTokens,
    setEnvironment,
    site,
    journeyId,
    vertical,
    affclie
  ]);
  const headerComponentProps = getPropsByName({
    name: "header",
    components
  });
  const progressComponentProps = getPropsByName({
    name: "progress-steps",
    components
  });
  const preFooterComponentProps = getPropsByName({
    name: "pre-footer",
    components
  });
  const footerComponentProps = getPropsByName({
    name: "footer",
    components
  });
  const initialRewardBannerProps = getPropsByName({
    name: "reward-banner",
    components
  });
  const indicativePricingModalInterventionProps = getPropsByName({
    name: "indicative-pricing-modal-intervention",
    components
  });
  useSetRewardBanner(initialRewardBannerProps);
  const [rewardBannerComponentProps] = useRewardBanner();
  return /* @__PURE__ */ jsxs23(
    "div",
    {
      className: "mobius-page__container",
      "data-page-loaded": pageReady,
      "data-testid": "base-page",
      children: [
        rewardBannerComponentProps && /* @__PURE__ */ jsx33(RewardBanner, { ...rewardBannerComponentProps }),
        /* @__PURE__ */ jsx33(Header2, { ...headerComponentProps }),
        indicativePricingModalInterventionProps && /* @__PURE__ */ jsx33(
          IndicativePricingModalIntervention,
          {
            ...indicativePricingModalInterventionProps
          }
        ),
        progressComponentProps && /* @__PURE__ */ jsx33(ProgressSteps, { ...progressComponentProps }),
        children,
        preFooterComponentProps && /* @__PURE__ */ jsx33(PreFooter, { ...preFooterComponentProps }),
        /* @__PURE__ */ jsx33(Footer4, { ...footerComponentProps })
      ]
    }
  );
};

// src/pages/Page/PageProviders.tsx
import { useMemo } from "react";
import { InterventionsProvider } from "@simplybusiness/mobius-interventions";
import { getSnowplowConfig, SnowplowProvider } from "@simplybusiness/services";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { Provider } from "jotai";

// src/constants/interventions.ts
var INACTIVITY_TIME = 5e3;

// src/pages/Page/PageProviders.tsx
import { jsx as jsx34, jsxs as jsxs24 } from "react/jsx-runtime";
var isCTM = (site) => site === "comparethemarket";
function PageProviders(props) {
  const { configuration } = props;
  const { site } = configuration;
  const snowplowProps = getSnowplowConfig(props);
  const duid = snowplowProps?.duid;
  const zendeskProps = getZendeskKeys(props);
  const queryClient = useMemo(() => new QueryClient(), []);
  const disablePolling = configuration.disableIdmPolling === "true";
  const ctmScriptProps = isCTM(site) ? getCtmScriptProps(props) : void 0;
  const tealiumScriptProps = isCTM(site) ? getTealiumScriptProps(props) : void 0;
  return /* @__PURE__ */ jsx34(Provider, { children: /* @__PURE__ */ jsxs24(QueryClientProvider, { client: queryClient, children: [
    zendeskProps && /* @__PURE__ */ jsx34(ZendeskProvider, { ...zendeskProps, experiments: props.experiments }),
    ctmScriptProps && /* @__PURE__ */ jsx34(CtmTrackingProvider, { ...ctmScriptProps }),
    tealiumScriptProps && /* @__PURE__ */ jsx34(CtmTealiumProvider, { ...tealiumScriptProps }),
    /* @__PURE__ */ jsx34(SnowplowProvider, { scripts: snowplowProps, children: /* @__PURE__ */ jsx34(
      InterventionsProvider,
      {
        site,
        duid,
        inactivityTime: INACTIVITY_TIME,
        disablePolling,
        children: /* @__PURE__ */ jsx34(ErrorBoundary, { children: /* @__PURE__ */ jsx34(TrustpilotProvider, { children: props.children }) })
      }
    ) }),
    /* @__PURE__ */ jsx34(ReactQueryDevtools, { initialIsOpen: false })
  ] }) });
}

export {
  Underline,
  LINK_ADDRESS_QUESTION_NAME,
  LINKED_ADDRESS_QUESTION_NAME,
  QUESTIONS_WITH_TOGGLE,
  ERRORS,
  ANNUAL,
  YEARLY,
  MONTHLY,
  AIRBRAKE_PROJECT_ID,
  AIRBRAKE_PROJECT_KEY,
  FOCUSABLE_ELEMENT_SELECTOR,
  BUSINESS_CITY_KEY,
  US_STATE_ANSWER_KEY,
  LOCALE_EN_GB,
  PREPOP_ENDPOINT_URL,
  creditRatingAgencyDescriptions,
  sliderStateAtom,
  paymentToggleAtom,
  quoteComparisonPageAtom,
  questionnaireFormUrlAtom,
  qcpLayoutToggleAtom,
  redirectAtom,
  seeMoreCoversStateAtom,
  getPropsByName,
  isObject,
  mapToArray,
  shallowEqual,
  formatDateByLocale,
  toISODate,
  transformAnswers,
  isOneOf,
  titleCase,
  snakeCase,
  toPascalCase,
  camelToTitleCase,
  snakeCaseKeys,
  isCurrency,
  containsHtmlMarkup,
  useExperiment,
  useTokens,
  useTokensValues,
  useLocale,
  ErrorBoundary,
  Footer4 as Footer,
  OPENING_HOURS_LABEL,
  OPENING_HOURS_US,
  OPENING_HOURS_UK,
  LOGO_LINK,
  Header2 as Header,
  InsuranceBenefits,
  PreFooter,
  ProgressStepsItem,
  ProgressSteps,
  useEnvironmentValues,
  TrustpilotProvider,
  useRewardBanner,
  Page,
  PageProviders
};
